import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const GOLD = "#C9A84C";
const DARK = "#1a1a1a";
const CARD = "#242424";
const BORDER = "#2e2e2e";
const MUTED = "#888";
const TEXT = "#f0ede8";
const BG_CARD = "#1a1a1a";
const MAHOGANY = "#8B4A3A";

const styles = {
  app: { fontFamily: "'Inter', sans-serif", background: DARK, color: TEXT, minHeight: "100vh", display: "flex", flexDirection: "column" },
  header: { background: "#111", borderBottom: `1px solid ${BORDER}`, padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60 },
  logo: { fontFamily: "'Cormorant Garant', serif", fontSize: 18, fontWeight: "normal", color: GOLD, letterSpacing: "0.12em", textTransform: "uppercase" },
  badge: { background: MAHOGANY, color: "#fff", fontSize: 10, padding: "3px 10px", borderRadius: 2, letterSpacing: "0.1em", textTransform: "uppercase" },
  nav: { background: "#161616", borderBottom: `1px solid ${BORDER}`, display: "flex", padding: "0 32px" },
  navTab: (active) => ({ padding: "14px 22px", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", color: active ? GOLD : MUTED, borderBottom: active ? `2px solid ${GOLD}` : "2px solid transparent", background: "none", border: "none", fontFamily: "'Cormorant Garant', serif", transition: "color 0.15s" }),
  body: { flex: 1, padding: "32px" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 },
  grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20, marginBottom: 24 },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 24 },
  statCard: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "22px 24px" },
  statLabel: { fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: MUTED, marginBottom: 10 },
  statValue: { fontSize: 28, color: GOLD, fontFamily: "'Cormorant Garant', serif", fontWeight: "normal" },
  statSub: { fontSize: 11, color: MUTED, marginTop: 4 },
  card: { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 4, overflow: "hidden", marginBottom: 20 },
  cardHeader: { padding: "16px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardTitle: { fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: MUTED, padding: "12px 24px", textAlign: "left", borderBottom: `1px solid ${BORDER}`, fontWeight: "normal" },
  td: { padding: "14px 24px", fontSize: 13, borderBottom: `1px solid #1f1f1f`, color: TEXT, verticalAlign: "middle" },
  pill: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 2, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", background: color + "22", color: color, border: `1px solid ${color}44` }),
  btn: { background: GOLD, color: "#111", border: "none", padding: "9px 20px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2, fontFamily: "'Cormorant Garant', serif" },
  btnGhost: { background: "none", color: GOLD, border: `1px solid ${GOLD}44`, padding: "7px 16px", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2, fontFamily: "'Cormorant Garant', serif" },
  sectionTitle: { fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: MUTED, marginBottom: 16, marginTop: 0 },
};

const statusColors = { Active: "#4CAF82", Ending: "#E8A838", Renewing: GOLD, Overdue: MAHOGANY, Deployed: "#4CAF82", Available: GOLD, Maintenance: MAHOGANY, Collected: "#4CAF82", Failed: MAHOGANY, Pending: GOLD, Ended: MUTED };
const Pill = ({ label }) => <span style={styles.pill(statusColors[label] || MUTED)}>{label}</span>;
function useIsMobile() {
  const q = "(max-width: 767px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = e => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

const MOBILE_MAIN = ["Dashboard", "Customers", "Machines", "Enquiries"];
const MOBILE_MORE = ["Systems", "Finance", "Growth", "Investment"];
const TAB_ICONS = { Dashboard: "◈", Customers: "☺", Machines: "▤", Enquiries: "✉", More: "⋯" };

const TABS = ["Dashboard", "Customers", "Machines", "Enquiries", "Systems", "Finance", "Growth", "Investment"];

const inputStyle = { width: "100%", background: "#1a1a1a", border: `1px solid ${BORDER}`, borderRadius: 2, padding: "9px 12px", color: TEXT, fontSize: 13, fontFamily: "'Cormorant Garant', serif", outline: "none", boxSizing: "border-box" };
const selectStyle = { ...inputStyle, cursor: "pointer", appearance: "none", WebkitAppearance: "none" };
const labelStyle = { fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, display: "block", marginBottom: 6 };
const fieldWrap = { marginBottom: 14 };

// ── DASHBOARD ───────────────────────────────────────────
function Dashboard({ customers, machines, onSelectCustomer, dismissedAlerts, setDismissedAlerts, alertsFromDb, setAlertsFromDb, important, onDeleteImportant, onSelectTab, monthlyOut = 0 }) {
  const mrr = customers.filter(c => c.status === "Active" || c.status === "Ending").reduce((s, c) => {
    const v = parseInt((c.monthly || "0").replace(/[£,]/g, ""));
    return s + (isNaN(v) ? 0 : v);
  }, 0);

  const reformerMachines = machines.filter(m => m.model !== "RR Chair");
  const chairMachines = machines.filter(m => m.model === "RR Chair");

  const today = new Date();

  // eslint-disable-next-line
  const sixWeekAlerts = customers.filter(c => {
    if (!c.start_date || c.status === "Ended") return false;
    const start = new Date(c.start_date);
    if (isNaN(start)) return false;
    const day42 = new Date(start); day42.setDate(day42.getDate() + 42);
    const day56 = new Date(start); day56.setDate(day56.getDate() + 50);
    return today >= day42 && today <= day56;
  }).map(c => ({
    label: `6-week follow up · ${c.name}`,
    detail: `Started ${new Date(c.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · Time to check in`,
    color: "#4CAF82",
    id: c.id
  }));

  // eslint-disable-next-line
  const staticAlerts = [];

  // eslint-disable-next-line
  const renewalAlerts = customers.filter(c => {
    if (!c.renewal || c.status === "Ended") return false;
    const d = new Date(c.renewal);
    if (isNaN(d)) return false;
    const daysUntil = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil >= 0;
  }).map(c => {
    const d = new Date(c.renewal);
    const daysUntil = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    return { label: `Renewal due · ${c.name}`, detail: `${daysUntil} days`, color: GOLD, id: c.id };
  });

  const [confirmDismiss, setConfirmDismiss] = useState(null);
  const [confirmImportantDelete, setConfirmImportantDelete] = useState(null);
  const visibleAlerts = alertsFromDb;

  return (
    <div>
      <p style={styles.sectionTitle}>Overview · {today.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</p>
      <div style={{ display: "grid", gridTemplateColumns: chairMachines.length > 0 ? "repeat(5, 1fr)" : "repeat(4, 1fr)", gap: 20, marginBottom: 24 }}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Net Monthly</div>
          <div style={styles.statValue}>£{Math.max(0, mrr - monthlyOut).toLocaleString()}</div>
          <div style={styles.statSub}>£{mrr.toLocaleString()} revenue · £{monthlyOut.toLocaleString()} outgoings</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Machines</div>
          <div style={styles.statValue}>{reformerMachines.filter(m => m.status === "Deployed").length} / {reformerMachines.length}</div>
          <div style={styles.statSub}>Deployed fleet</div>
        </div>
        {chairMachines.length > 0 && (
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Active Chairs</div>
            <div style={styles.statValue}>{chairMachines.filter(m => m.status === "Deployed").length} / {chairMachines.length}</div>
            <div style={styles.statSub}>Deployed chairs</div>
          </div>
        )}
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Available Stock</div>
          <div style={styles.statValue}>{reformerMachines.filter(m => m.status === "Available").length}</div>
          <div style={styles.statSub}>Ready to deploy</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Customers</div>
          <div style={styles.statValue}>{customers.filter(c => c.status === "Active").length}</div>
          <div style={styles.statSub}>{customers.length} all time</div>
        </div>
      </div>

      {(() => {
        const totalPurchase = machines.reduce((sum, m) => sum + parseFloat((m.purchase_price || 0)), 0);
        if (totalPurchase === 0) return null;
        const today = new Date();
        const totalEarned = customers.reduce((sum, cust) => {
          const start = new Date(cust.start_date);
          if (isNaN(start)) return sum;
          const endRaw = cust.status === "Ended" && cust.renewal ? new Date(cust.renewal) : today;
          const end = new Date(endRaw.getFullYear(), endRaw.getMonth(), endRaw.getDate(), 23, 59, 59);
          const history = (window.__rateHistories || []).filter(h => h.customer_id === cust.id).sort((a, b) => new Date(a.from_date) - new Date(b.from_date));
          if (history.length === 0) {
            const rate = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
            if (isNaN(rate)) return sum;
            let months = 0;
            let d = new Date(start); d.setMonth(d.getMonth() + 1);
            while (d <= end) { months++; d.setMonth(d.getMonth() + 1); }
            const extraP3 = cust.extra_payments || 0;
            const netVal = (months * rate) + (extraP3 * rate) + parseFloat(cust.delivery_charge || 0) + parseFloat(cust.collection_charge || 0) - parseFloat(cust.expenses || 0);
            return sum + netVal;
          }
          let custTotal = 0;
          const firstFrom = new Date(history[0].from_date);
          if (firstFrom > start) {
            const rate = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
            let d = new Date(start); d.setMonth(d.getMonth() + 1);
            while (d <= firstFrom && d <= end) { custTotal += rate; d.setMonth(d.getMonth() + 1); }
          }
          history.forEach(h => {
            const pStart = new Date(h.from_date);
            const pEndRaw = h.to_date ? new Date(h.to_date) : end;
            const pEnd = pEndRaw < end ? new Date(pEndRaw.getFullYear(), pEndRaw.getMonth(), pEndRaw.getDate(), 23, 59, 59) : end;
            let d = new Date(pStart); d.setMonth(d.getMonth() + 1);
            while (d <= pEnd) { custTotal += h.rate; d.setMonth(d.getMonth() + 1); }
          });
          const extraP2 = cust.extra_payments || 0;
          const baseRate2 = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
          custTotal += extraP2 * baseRate2;
          custTotal += parseFloat(cust.delivery_charge || 0);
          custTotal += parseFloat(cust.collection_charge || 0);
          custTotal -= parseFloat(cust.expenses || 0);
          return sum + custTotal;
        }, 0);
        const pct = Math.min(100, Math.round((totalEarned / totalPurchase) * 100));
        const remaining = Math.max(0, totalPurchase - totalEarned);
        return (
          <div style={{ ...styles.statCard, marginBottom: 24 }}>
            <div style={styles.statLabel}>Fleet Payoff Tracker</div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ ...styles.statValue, fontSize: 22 }}>£{Math.round(totalEarned).toLocaleString()} <span style={{ fontSize: 13, color: MUTED }}>earned</span></span>
              <span style={{ fontSize: 13, color: MUTED, alignSelf: "flex-end" }}>£{Math.round(remaining).toLocaleString()} remaining</span>
            </div>
            <div style={{ background: "#2a2a2a", borderRadius: 2, height: 8, width: "100%", marginBottom: 8 }}>
              <div style={{ background: pct >= 100 ? "#4CAF82" : GOLD, height: 8, borderRadius: 2, width: pct + "%", transition: "width 0.4s ease" }} />
            </div>
            <div style={{ fontSize: 11, color: pct >= 100 ? "#4CAF82" : MUTED }}>{pct}% paid off · Total fleet cost £{Math.round(totalPurchase).toLocaleString()}</div>
          </div>
        );
      })()}

      <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
        {[
          { label: "GoCardless", url: "https://manage.gocardless.com" },
          { label: "Google Drive", url: "https://drive.google.com" },
          { label: "ReformRentals.com", url: "https://reformrentals.com" },
          { label: "Outlook", url: "https://outlook.live.com" },
        ].map(l => (
          <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED, textDecoration: "none", borderBottom: `1px solid ${BORDER}`, paddingBottom: 2 }}>
            {l.label} ↗
          </a>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>Alerts</span>
            <span style={{ fontSize: 10, color: MUTED }}>{visibleAlerts.length} active</span>
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto", padding: "8px 0" }}>
            {visibleAlerts.length === 0 && <div style={{ padding: "20px 24px", fontSize: 12, color: MUTED }}>No active alerts</div>}
            {visibleAlerts.map((a, i) => {
              // Dynamic countdown for renewal alerts
              let displayDetail = a.detail;
              console.log("Alert:", a.label, "Type:", a.type, "CustomerID:", a.customer_id);
              if (a.type === "renewal" && a.customer_id) {
                const customer = customers.find(c => c.id === a.customer_id);
                console.log("Found customer:", customer?.name, "Renewal:", customer?.renewal);
                if (customer && customer.renewal) {
                  const renewalDate = new Date(customer.renewal);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const daysUntil = Math.round((renewalDate - today) / (1000 * 60 * 60 * 24));
                  console.log("Calculated days:", daysUntil);
                  displayDetail = `${daysUntil} days until renewal`;
                }
              }
              return (
              <div key={i} style={{ padding: "14px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 3, height: 36, background: a.color, borderRadius: 2, flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, cursor: a.customer_id ? "pointer" : "default" }} onClick={() => a.customer_id && onSelectCustomer(a.customer_id)}>
                  <div style={{ fontSize: 13, color: a.customer_id ? GOLD : TEXT }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{displayDetail}{a.customer_id && " →"}</div>
                </div>
                {confirmDismiss === a.id ? (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button onClick={() => { supabase.from("alerts").delete().eq("id", a.id).then(({error}) => { console.log("delete error:", error); }); const dl = JSON.parse(localStorage.getItem("dismissedAlerts") || "[]"); dl.push(a.label); localStorage.setItem("dismissedAlerts", JSON.stringify(dl)); setAlertsFromDb(prev => prev.filter(x => x.id !== a.id)); setConfirmDismiss(null); }} style={{ background: "none", border: `1px solid ${MAHOGANY}66`, color: MAHOGANY, cursor: "pointer", fontSize: 10, padding: "3px 8px", borderRadius: 2, fontFamily: "'Cormorant Garant', serif", letterSpacing: "0.1em" }}>Sure?</button>
                    <button onClick={() => setConfirmDismiss(null)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 11, padding: "0 4px" }}>✕</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDismiss(a.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14, padding: "0 4px" }}>✕</button>
                )}
              </div>
              );
            })}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <span style={{ ...styles.cardTitle, color: MAHOGANY }}>⚠ Important</span>
            <button onClick={() => onSelectTab("Growth")} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px" }}>See all →</button>
          </div>
          <div style={{ maxHeight: 160, overflowY: "auto", padding: "8px 0" }}>
            {(important||[]).length === 0 && <div style={{ padding: "20px 24px", fontSize: 12, color: MUTED }}>No important notes</div>}
            {(important||[]).map(item => (
              <div key={item.id} style={{ padding: "10px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div style={{ width: 3, minHeight: 32, background: MAHOGANY, borderRadius: 2, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12, color: TEXT, lineHeight: 1.5, wordBreak: "break-word", overflowWrap: "anywhere" }}>{item.text}</span>
                {confirmImportantDelete === item.id ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => { onDeleteImportant(item.id); setConfirmImportantDelete(null); }} style={{ background: "none", border: `1px solid ${MAHOGANY}66`, color: MAHOGANY, cursor: "pointer", fontSize: 10, padding: "2px 6px", borderRadius: 2, fontFamily: "'Cormorant Garant', serif" }}>Sure?</button>
                    <button onClick={() => setConfirmImportantDelete(null)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 11 }}>✕</button>
                  </div>
                ) : (
                  <button onClick={() => setConfirmImportantDelete(item.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}><span style={styles.cardTitle}>Recent Customers</span></div>
          <table style={styles.table}>
            <thead><tr>{["Name", "Type", "Monthly", "Status"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {[...customers].sort((a, b) => parseInt(b.id) - parseInt(a.id)).slice(0, 3).map(c => (
                <tr key={c.id} onClick={() => onSelectCustomer(c.id)} style={{ cursor: "pointer" }}>
                  <td style={styles.td}>{c.name}</td>
                  <td style={{ ...styles.td, color: MUTED }}>{c.type}</td>
                  <td style={{ ...styles.td, color: GOLD }}>{c.monthly}</td>
                  <td style={styles.td}><Pill label={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── CUSTOMERS ───────────────────────────────────────────

// ── FILE UPLOAD ─────────────────────────────────────────
function CustomerNotesSection({ customerId }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: open ? 16 : 0 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: MUTED }}>Notes</div>
        <span style={{ color: MUTED, fontSize: 13 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && <CustomerNotes customerId={customerId} />}
    </div>
  );
}

function CustomerNotes({ customerId }) {
  const [notes, setNotes] = useState([]);
  const [input, setInput] = useState("");

  useEffect(() => {
    supabase.from("customer_notes").select("*").eq("customer_id", customerId).order("created_at", { ascending: false }).then(({ data }) => {
      if (data) setNotes(data);
    });
  }, [customerId]);

  const add = async () => {
    if (!input.trim()) return;
    const { data } = await supabase.from("customer_notes").insert({ customer_id: customerId, text: input.trim() }).select().single();
    if (data) {
      setNotes(prev => [data, ...prev]);
      if (window.__setLastAction) window.__setLastAction({ type: "delete_note", table: "customer_notes", id: data.id, prev: data, restore: (d) => setNotes(p => [d, ...p]) });
    }
    setInput("");
  };

  const remove = async (id) => {
    const prev = notes.find(n => n.id === id);
    await supabase.from("customer_notes").delete().eq("id", id);
    setNotes(prev => prev.filter(n => n.id !== id));
    if (window.__setLastAction) window.__setLastAction({ type: "insert_note", table: "customer_notes", prev, restore: (d) => setNotes(p => [d, ...p]) });
  };

  return (
    <div>
      {notes.map(n => (
        <div key={n.id} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12, padding: "12px 0", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ width: 3, minHeight: 20, background: GOLD, borderRadius: 2, flexShrink: 0, marginTop: 3 }} />
          <span style={{ flex: 1, fontSize: 13, color: TEXT, lineHeight: 1.8, fontFamily: "'Cormorant Garant', serif", whiteSpace: "pre-wrap" }}>{n.text.replace(/ \* /g, "\n").replace(/\*$/g, "")}</span>
          <button onClick={() => remove(n.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14, flexShrink: 0 }}>✕</button>
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Add a note..." rows={4}
          style={{ ...inputStyle, width: "100%", resize: "vertical", lineHeight: 1.7, minHeight: 100, boxSizing: "border-box" }} />
        <button onClick={add} style={{ ...styles.btnGhost, marginTop: 8 }}>Add</button>
      </div>
    </div>
  );
}

function FileUpload({ customerId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    supabase.storage.from("customer-files").list(customerId + "/").then(({ data }) => {
      if (data) setFiles(data);
    });
  }, [customerId]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const path = customerId + "/" + Date.now() + "_" + file.name;
    await supabase.storage.from("customer-files").upload(path, file);
    const { data } = await supabase.storage.from("customer-files").list(customerId + "/");
    if (data) setFiles(data);
    setUploading(false);
  };

  const getUrl = (name) => {
    return `https://ivuzxepgsdrvrszvgxdq.supabase.co/storage/v1/object/public/customer-files/${customerId}/${name}`;
  };

  const remove = async (name) => {
    await supabase.storage.from("customer-files").remove([customerId + "/" + name]);
    setFiles(prev => prev.filter(f => f.name !== name));
  };

  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);

  return (
    <div>
      {files.map(f => (
        <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          {isImage(f.name)
            ? <img src={getUrl(f.name)} alt={f.name} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 2, border: `1px solid ${BORDER}` }} />
            : <div style={{ width: 48, height: 48, background: "#1a1a1a", border: `1px solid ${BORDER}`, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: MUTED }}>PDF</div>
          }
          <a href={getUrl(f.name)} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: 12, color: GOLD, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name.replace(/^\d+_/, "")}</a>
          <button onClick={() => remove(f.name)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      ))}
      <label style={{ display: "inline-block", marginTop: 8, cursor: "pointer" }}>
        <input type="file" onChange={upload} style={{ display: "none" }} accept="image/*,.pdf" />
        <span style={{ ...styles.btnGhost, fontSize: 10, padding: "6px 14px" }}>{uploading ? "Uploading..." : "+ Add File"}</span>
      </label>
    </div>
  );
}

function Customers({ customers, setCustomers, machines, setMachines, focusId, clearFocus, onSelectMachine, setLastAction, rateHistories, setRateHistories }) {
  const [selected, setSelected] = useState(focusId || null);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [sortAsc, setSortAsc] = useState(true);
  const [sortField, setSortField] = useState("renewal");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const blank = { id: "", name: "", business: "", type: "Train", location: "", machine: "", term: "Rolling", monthly: "", status: "Active", start_date: "", renewal: "" };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => { if (focusId) { setSelected(focusId); clearFocus(); } }, [focusId]); // eslint-disable-line

  const c = selected ? customers.find(x => x.id === selected) : null;

  const calcEndDate = (start_date, months) => {
    if (!start_date || !months) return "";
    const d = new Date(start_date);
    d.setMonth(d.getMonth() + parseInt(months));
    return d.toISOString().split("T")[0];
  };

  const isRolling = form.term === "Rolling";
  const termMonths = form.term === "other" ? form.customTerm : form.term;
  const endDate = isRolling ? "" : calcEndDate(form.start_date, termMonths);

  const sortedMachines = [...(machines || [])].sort((a, b) => {
    const parse = id => { const m = id.match(/^([A-Z]+)(\d+)$/); return m ? [m[1], parseInt(m[2])] : [id, 0]; };
    const [aL, aN] = parse(a.id); const [bL, bN] = parse(b.id);
    if (aL === "R" && bL !== "R") return -1;
    if (aL !== "R" && bL === "R") return 1;
    return aN - bN;
  });

  const save = async () => {
    if (!form.name.trim()) return;
    if (form.term === "other" && !form.customTerm) return;
    const monthly = form.monthly.startsWith("£") ? form.monthly : `£${form.monthly}`;
    const renewal = isRolling ? form.start_date : endDate;
    const term = isRolling ? "Rolling" : `${termMonths}mo`;
    const newRow = { ...form, monthly, term, renewal, original_end_date: renewal };
    const { data, error } = await supabase.from("customers").insert([newRow]).select();
    if (!error && data) {
      setCustomers(prev => [...prev, data[0]]);
      if (form.machine) {
        await supabase.from("machines").update({ customer: form.name, status: "Deployed" }).eq("id", form.machine);
        setMachines(prev => prev.map(m => m.id === form.machine ? { ...m, customer: form.name, status: "Deployed" } : m));
      }
    }
    setForm(blank); setShowForm(false);
  };

  const saveEdit = async () => {
    const prev = customers.find(x => x.id === c.id);
    const toSave = { ...editForm };
    const newChairIds = (toSave.machine || "").split(",").map(x => x.trim()).filter(Boolean)
      .filter(mid => !(prev.machine || "").split(",").map(x => x.trim()).includes(mid))
      .filter(mid => { const mach = (machines || []).find(mm => mm.id === mid); return mach && mach.model === "RR Chair"; });
    if (newChairIds.length > 0) {
      const chairSum = newChairIds.reduce((s, mid) => s + (parseFloat((toSave.chair_rates && toSave.chair_rates[mid]) || 0) || 0), 0);
      const baseNum = parseFloat((toSave.monthly || "0").replace(/[£,]/g, "")) || 0;
      toSave.monthly = "£" + (baseNum + chairSum);
      toSave.chair_monthly = (parseFloat(prev.chair_monthly || 0) || 0) + chairSum;
      for (const mid of newChairIds) {
        await supabase.from("machines").update({ customer: toSave.name, status: "Deployed" }).eq("id", mid);
        setMachines(prevM => prevM.map(m => m.id === mid ? { ...m, customer: toSave.name, status: "Deployed" } : m));
      }
    }
    delete toSave.chair_rates;
    const newRate = toSave.newExtensionRate;
    const transitionDate = toSave.pendingRateTransition;
    const oldMonthly = prev.monthly;
    delete toSave.newExtensionRate;
    delete toSave.extensionDate;
    delete toSave.pendingRateTransition;
    delete toSave.extensionActive;

    const oldRateNum = parseFloat((oldMonthly || "0").replace(/[£,]/g, ""));
    const newRateNum = parseFloat(newRate || "0");

    if (transitionDate && newRate && !isNaN(newRateNum)) {
      const { data: existingHistory } = await supabase.from("rate_history").select("*").eq("customer_id", c.id).order("from_date", { ascending: true });
      const openRow = (existingHistory || []).find(h => !h.to_date);
      if (openRow) {
        await supabase.from("rate_history").update({ to_date: transitionDate }).eq("id", openRow.id);
      } else if (prev.start_date) {
        await supabase.from("rate_history").insert({ customer_id: c.id, rate: oldRateNum, from_date: prev.start_date, to_date: transitionDate });
      }
      await supabase.from("rate_history").insert({ customer_id: c.id, rate: newRateNum, from_date: transitionDate, to_date: null });
      const { data: refreshed } = await supabase.from("rate_history").select("*");
      if (refreshed && setRateHistories) setRateHistories(refreshed);
      toSave.extension_rate = "£" + newRateNum;
      const todayForTransition = new Date(); todayForTransition.setHours(0, 0, 0, 0);
      const transitionIsFuture = new Date(transitionDate) > todayForTransition;
      if (!transitionIsFuture) {
        toSave.monthly = "£" + newRateNum;
      } else {
        toSave.monthly = oldMonthly;
      }
      if (!prev.original_monthly_rate) {
        toSave.original_monthly_rate = oldMonthly;
      }
    } else if (toSave.extension_rate && prev.extension_rate && toSave.extension_rate !== prev.extension_rate && toSave.extension_start_date) {
      const newExtRateNum = parseFloat((toSave.extension_rate || "0").replace(/[£,]/g, ""));
      const { data: existingHistory } = await supabase.from("rate_history").select("*").eq("customer_id", c.id).order("from_date", { ascending: true });
      const openRow = (existingHistory || []).find(h => !h.to_date);
      if (openRow && openRow.from_date === toSave.extension_start_date) {
        await supabase.from("rate_history").update({ rate: newExtRateNum }).eq("id", openRow.id);
        const { data: refreshed } = await supabase.from("rate_history").select("*");
        if (refreshed && setRateHistories) setRateHistories(refreshed);
      }
    }

    // Keep the rate history in step with a manual monthly-rate edit, otherwise the
    // load-time rate sync puts the old rate back on refresh
    if (!(transitionDate && newRate && !isNaN(newRateNum))) {
      const prevNum = parseFloat((prev.monthly || "0").replace(/[£,]/g, ""));
      const nowNum = parseFloat((toSave.monthly || "0").replace(/[£,]/g, ""));
      if (!isNaN(nowNum) && nowNum !== prevNum) {
        const todayStr = new Date().toISOString().split("T")[0];
        const { data: rows } = await supabase.from("rate_history").select("*").eq("customer_id", c.id).order("from_date", { ascending: false });
        const current = (rows || []).find(h => h.from_date <= todayStr);
        if (current) {
          await supabase.from("rate_history").update({ rate: nowNum }).eq("id", current.id);
          const { data: refreshed } = await supabase.from("rate_history").select("*");
          if (refreshed && setRateHistories) setRateHistories(refreshed);
        }
      }
    }

    const { error } = await supabase.from("customers").update(toSave).eq("id", c.id);
    if (!error) {
      setLastAction({ type: "edit", table: "customers", prev, id: c.id });
      setCustomers(prev => prev.map(x => x.id === c.id ? { ...x, ...toSave } : x));
      if (toSave.machine) {
        await supabase.from("machines").update({ customer: toSave.name, status: "Deployed" }).eq("id", toSave.machine);
        setMachines(prev => prev.map(m => m.id === toSave.machine ? { ...m, customer: toSave.name, status: "Deployed" } : m));
      }
    }
    setEditMode(false);
  };

  const deleteCustomer = async () => {
    const prev = customers.find(x => x.id === c.id);
    await supabase.from("customers").delete().eq("id", c.id);
    setLastAction({ type: "delete", table: "customers", prev, id: prev.id });
    setCustomers(prev => prev.filter(x => x.id !== c.id));
    setSelected(null); setConfirmDelete(null);
  };



  const getDisplayRenewal = (row) => {
    if (row.term !== "Rolling") return row.renewal ? new Date(row.renewal).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
    if (!row.renewal) return "—";
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let d = new Date(row.renewal);
    if (isNaN(d)) return row.renewal;
    while (d < today) d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const parseRenewal = (row) => {
    if (!row.renewal) return Infinity;
    const d = new Date(row.renewal);
    return isNaN(d) ? Infinity : d.getTime();
  };

  const activeCustomers = customers.filter(r => r.status !== "Ended" && r.status !== "Pending");

  const endedCustomers = customers.filter(r => r.status === "Ended");
  const parseId = (id) => { const n = parseInt((id || "").toString().replace(/\D/g, "")); return isNaN(n) ? Infinity : n; };
  const sortedActive = [...activeCustomers].sort((a, b) => {
    if (sortField === "renewal") return sortAsc ? parseRenewal(a) - parseRenewal(b) : parseRenewal(b) - parseRenewal(a);
    return parseId(a.id) - parseId(b.id);
  });
  const [endedOpen, setEndedOpen] = useState(false);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>All Customers · {customers.length} total</p>
        <button style={styles.btn} onClick={() => { setShowForm(true); setSelected(null); }}>+ Add Customer</button>
      </div>
      <div style={styles.grid2}>
        <div>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={{ ...styles.cardTitle, color: "#4CAF82" }}>Active · {activeCustomers.length}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { setSortField("renewal"); setSortAsc(s => !s); }} style={{ ...styles.btnGhost, padding: "4px 10px", fontSize: 10, ...(sortField === "renewal" ? { color: GOLD, borderColor: GOLD } : {}) }}>End date {sortField === "renewal" ? (sortAsc ? "↑" : "↓") : ""}</button>
                <button onClick={() => setSortField("id")} style={{ ...styles.btnGhost, padding: "4px 10px", fontSize: 10, ...(sortField === "id" ? { color: GOLD, borderColor: GOLD } : {}) }}>ID</button>
              </div>
            </div>
            <table style={styles.table}>
              <thead><tr>{["ID", "Name", "Type", "End Date"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {sortedActive.map(row => (
                  <tr key={row.id} onClick={() => { setSelected(row.id); setShowForm(false); setEditMode(false); }} style={{ cursor: "pointer", background: selected === row.id ? "#2a2a2a" : "transparent" }}>
                    <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.id}</td>
                    <td style={styles.td}>{row.name}</td>
                    <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.type}</td>
                    <td style={{ ...styles.td, fontSize: 12 }}>
                      <span style={{ color: GOLD }}>{getDisplayRenewal(row)}</span>
                      {row.status !== "Ending" && (row.term === "Rolling" || row.extension === "Rolling") && <span style={{ color: "#4CAF82", marginLeft: 6, fontSize: 11 }}>R</span>}{row.auto_rolled && <span style={{ color: GOLD, marginLeft: 4, fontSize: 11 }}>⭐</span>}{row.extension && row.extension !== "Rolling" && <span style={{ color: GOLD, marginLeft: 6, fontSize: 11 }}>+{row.extension}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {customers.filter(r => r.status === "Pending").length > 0 && (
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={{ ...styles.cardTitle, color: "#6B8FA8" }}>Pending · {customers.filter(r => r.status === "Pending").length}</span>
              </div>
              <table style={styles.table}>
                <thead><tr>{["ID", "Name", "Type", "Start Date"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {customers.filter(r => r.status === "Pending").map(row => (
                    <tr key={row.id} onClick={() => { setSelected(row.id); setShowForm(false); setEditMode(false); }} style={{ cursor: "pointer", background: selected === row.id ? "#2a2a2a" : "transparent" }}>
                      <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.id}</td>
                      <td style={styles.td}>{row.name}</td>
                      <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.type}</td>
                      <td style={{ ...styles.td, color: "#6B8FA8", fontSize: 12 }}>{row.start_date ? new Date(row.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {endedCustomers.length > 0 && (
            <div style={styles.card}>
              <div style={{ ...styles.cardHeader, cursor: "pointer" }} onClick={() => setEndedOpen(o => !o)}>
                <span style={{ ...styles.cardTitle, color: MUTED }}>Ended · {endedCustomers.length}</span>
                <span style={{ color: MUTED, fontSize: 14 }}>{endedOpen ? "▲" : "▼"}</span>
              </div>
              {endedOpen && (
                <table style={styles.table}>
                  <thead><tr>{["ID", "Name", "Type", "End Date"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {endedCustomers.map(row => (
                      <tr key={row.id} onClick={() => { setSelected(row.id); setShowForm(false); setEditMode(false); }} style={{ cursor: "pointer", background: selected === row.id ? "#2a2a2a" : "transparent", opacity: 0.6 }}>
                        <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.id}</td>
                        <td style={styles.td}>{row.name}</td>
                        <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{row.type}</td>
                        <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{row.renewal ? new Date(row.renewal).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {showForm ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>New Customer</span>
              <button style={styles.btnGhost} onClick={() => setShowForm(false)}>✕ Cancel</button>
            </div>
            <div style={{ padding: 24 }}>
              {[["Customer ID", "id"], ["Name", "name"], ["Business / Studio", "business"], ["Location", "location"], ["Monthly Rate (£)", "monthly"]].map(([label, key]) => (
                <div key={key} style={fieldWrap}>
                  <label style={labelStyle}>{label}</label>
                  <input value={form[key]} onChange={e => set(key, e.target.value)} style={inputStyle} />
                </div>
              ))}
              <div style={fieldWrap}>
                <label style={labelStyle}>Machine</label>
                <select value={form.machine} onChange={e => set("machine", e.target.value)} style={selectStyle}>
                  <option value="">— None</option>
                  {sortedMachines.map(m => <option key={m.id} value={m.id}>{m.id} · {m.model}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Role</label>
                  <select value={form.type} onChange={e => set("type", e.target.value)} style={selectStyle}>
                    {["Train", "Teach", "T/T", "Studio"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Status</label>
                  <select value={form.status} onChange={e => set("status", e.target.value)} style={selectStyle}>
                    {["Active", "Pending", "Ending", "Ended"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Start Date</label>
                  <input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Contract Term</label>
                  <select value={form.term} onChange={e => set("term", e.target.value)} style={selectStyle}>
                    <option value="Rolling">Monthly (Rolling)</option>
                    {["4", "6", "8", "12", "14"].map(o => <option key={o} value={o}>{o} months</option>)}
                  </select>
                </div>
              </div>

              {isRolling && form.start_date && (
                <div style={{ marginBottom: 14, padding: "10px 14px", background: "#1a1a1a", borderRadius: 2, border: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <span style={{ color: MUTED }}>Rolling monthly from </span><span style={{ color: GOLD }}>{new Date(form.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
              )}
              {!isRolling && endDate && (
                <div style={{ marginBottom: 14, padding: "10px 14px", background: "#1a1a1a", borderRadius: 2, border: `1px solid ${BORDER}`, fontSize: 12 }}>
                  <span style={{ color: MUTED }}>Contract ends: </span><span style={{ color: GOLD }}>{new Date(endDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
              )}
              <button style={{ ...styles.btn, width: "100%", padding: 12 }} onClick={save}>Save Customer</button>
            </div>
          </div>
        ) : c ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>{editMode ? "Edit Customer" : "Customer Detail"}</span>
              <div style={{ display: "flex", gap: 8 }}>
                {!editMode && <button style={styles.btnGhost} onClick={() => { 
                  const hasExtension = c.extension_start_date && c.extension_start_date !== "";
                  setEditForm({ ...c, extensionActive: hasExtension, original_monthly_rate: c.original_monthly_rate || c.monthly }); 
                  setEditMode(true); 
                }}>Edit</button>}
                <button style={styles.btnGhost} onClick={() => { setSelected(null); setEditMode(false); }}>✕ Close</button>
              </div>
            </div>
            {editMode ? (
              <div style={{ padding: 24 }}>
                {[["Name", "name"], ["Business / Studio", "business"], ["Location", "location"], ["Monthly Rate (£)", "monthly"]].map(([label, key]) => (
                  <div key={key} style={fieldWrap}>
                    <label style={labelStyle}>{label}</label>
                    <input value={editForm[key] || ""} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} />
                  </div>
                ))}
                <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Machine(s)</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                      {(editForm.machine || "").split(",").map(m => m.trim()).filter(Boolean).map(mid => (
                        <span key={mid} style={{ fontSize: 11, color: GOLD, background: GOLD + "15", border: `1px solid ${GOLD}33`, padding: "3px 10px", borderRadius: 2, display: "flex", alignItems: "center", gap: 6 }}>
                          {mid}
                          <span onClick={() => setEditForm(f => ({ ...f, machine: f.machine.split(",").map(x => x.trim()).filter(x => x !== mid).join(", ") }))} style={{ cursor: "pointer", color: MUTED, fontSize: 13 }}>✕</span>
                        </span>
                      ))}
                    </div>
                    <select value="" onChange={e => { if (!e.target.value) return; setEditForm(f => { const existing = (f.machine || "").split(",").map(x => x.trim()).filter(Boolean); if (existing.includes(e.target.value)) return f; return { ...f, machine: [...existing, e.target.value].join(", ") }; }); }} style={selectStyle}>
                      <option value="">+ Add machine</option>
                      {sortedMachines.map(m => <option key={m.id} value={m.id}>{m.id} · {m.model}</option>)}
                    </select>
                    {(editForm.machine || "").split(",").map(x => x.trim()).filter(Boolean)
                      .filter(mid => !(c.machine || "").split(",").map(x => x.trim()).includes(mid))
                      .filter(mid => { const mach = (machines || []).find(mm => mm.id === mid); return mach && mach.model === "RR Chair"; })
                      .map(mid => (
                        <div key={mid} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                          <span style={{ fontSize: 11, color: GOLD, width: 44 }}>{mid} rate</span>
                          <span style={{ color: MUTED, fontSize: 12 }}>£</span>
                          <input type="number" value={(editForm.chair_rates && editForm.chair_rates[mid]) || ""} onChange={e => setEditForm(f => ({ ...f, chair_rates: { ...(f.chair_rates || {}), [mid]: e.target.value } }))} style={{ ...inputStyle, width: 100 }} placeholder="e.g. 40" />
                          <span style={{ fontSize: 10, color: MUTED }}>/mo · same contract as above</span>
                        </div>
                      ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Role</label>
                    <select value={editForm.type || "Home"} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))} style={selectStyle}>
                      {["Train", "Teach", "T/T", "Studio"].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Status</label>
                  <select value={editForm.status || "Active"} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} style={selectStyle}>
                    {["Active", "Pending", "Ending", "Ended"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: 20, padding: "16px", background: "#1a1a1a", border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Original Contract</div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Original Contract Term</label>
                    <div style={{ ...inputStyle, color: MUTED, cursor: "default" }}>{editForm.term || "—"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Original Start Date</label>
                      <input type="date" value={editForm.start_date || ""} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Original End Date</label>
                      <input type="date" value={editForm.original_end_date || ""} onChange={e => setEditForm(f => ({ ...f, original_end_date: e.target.value }))} style={inputStyle} />
                    </div>
                  </div>
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Original Monthly Rate (£)</label>
                    <input type="text" value={editForm.original_monthly_rate || editForm.monthly || ""} onChange={e => setEditForm(f => ({ ...f, original_monthly_rate: e.target.value }))} style={inputStyle} placeholder="e.g. £160" />
                  </div>
                </div>
                {!editForm.extensionActive ? (
                  <button style={{ ...styles.btn, width: "100%", marginBottom: 14 }} onClick={() => setEditForm(f => {
                    const base = f.original_end_date || f.renewal;
                    let startDate = base;
                    if (base) {
                      const d = new Date(base);
                      d.setDate(d.getDate() + 1);
                      startDate = d.toISOString().split("T")[0];
                    }
                    return { ...f, extensionActive: true, extension_start_date: startDate };
                  })}>+ Add Extension</button>
                ) : (
                  <div style={{ marginBottom: 20, padding: "16px", background: "#1a1a1a", border: `1px solid ${GOLD}44`, borderRadius: 2 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD }}>Extension</div>
                      <button style={{ background: "none", border: "none", color: MAHOGANY, cursor: "pointer", fontSize: 11 }} onClick={() => setEditForm(f => ({ ...f, extensionActive: false, extension: null, extension_start_date: null, extension_end_date: null, renewal: f.original_end_date, pendingRateTransition: null, newExtensionRate: null }))}>✕ Remove</button>
                    </div>
                    <div style={fieldWrap}>
                      <label style={labelStyle}>Extension Start Date</label>
                      <input type="date" value={editForm.extension_start_date || ""} onChange={e => setEditForm(f => ({ ...f, extension_start_date: e.target.value }))} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Extension Length</label>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {["Rolling", "4", "6", "8", "12", "other"].map(opt => (
                          <button key={opt} style={{ ...styles.btnGhost, padding: "5px 12px", fontSize: 10, ...(editForm.extension === opt ? { color: GOLD, borderColor: GOLD } : {}) }} onClick={() => {
                            const startDate = editForm.extension_start_date ? new Date(editForm.extension_start_date) : new Date();
                            const currentRate = (editForm.monthly || "0").replace(/[£,]/g, "");
                            if (opt === "Rolling") {
                              const rollingDate = new Date(startDate);
                              rollingDate.setMonth(rollingDate.getMonth() + 1);
                              setEditForm(f => ({ ...f, extension: "Rolling", extension_end_date: rollingDate.toISOString().split("T")[0], renewal: rollingDate.toISOString().split("T")[0], pendingRateTransition: f.extension_start_date, newExtensionRate: currentRate }));
                            } else if (opt === "other") {
                              setEditForm(f => ({ ...f, extension: "other", pendingRateTransition: f.extension_start_date, newExtensionRate: currentRate }));
                            } else {
                              const months = parseInt(opt);
                              const newEnd = new Date(startDate);
                              newEnd.setMonth(newEnd.getMonth() + months);
                              setEditForm(f => ({ ...f, extension: opt + "mo", extension_end_date: newEnd.toISOString().split("T")[0], renewal: newEnd.toISOString().split("T")[0], pendingRateTransition: f.extension_start_date, newExtensionRate: currentRate }));
                            }
                          }}>{opt === "Rolling" ? "Rolling" : opt === "other" ? "Other" : opt + " months"}</button>
                        ))}
                      </div>
                      {editForm.extension === "other" && (
                        <div style={{ marginTop: 8 }}>
                          <label style={labelStyle}>Pick End Date</label>
                          <input type="date" style={{ ...inputStyle, width: 200 }}
                            value={editForm.extension_end_date || ""}
                            onChange={e => {
                              if (!e.target.value) return;
                              setEditForm(f => ({ ...f, extension: "custom", extension_end_date: e.target.value, renewal: e.target.value }));
                            }} />
                        </div>
                      )}
                    </div>
                    {editForm.extension_end_date && (
                      <div style={fieldWrap}>
                        <label style={labelStyle}>Extension End Date</label>
                        <input type="date" value={editForm.extension_end_date || ""} onChange={e => setEditForm(f => ({ ...f, extension_end_date: e.target.value, renewal: e.target.value }))} style={inputStyle} />
                      </div>
                    )}
                    <div style={fieldWrap}>
                      <label style={labelStyle}>Extension Rate (£)</label>
                      <input type="text" value={editForm.extension_rate || ""} onChange={e => setEditForm(f => ({ ...f, extension_rate: e.target.value, monthly: e.target.value }))} style={inputStyle} placeholder="e.g. £130" />
                    </div>
                    {editForm.pendingRateTransition && (
                      <div style={{ marginTop: 12, padding: "12px 14px", background: "#0d0d0d", border: `1px solid ${GOLD}66`, borderRadius: 2 }}>
                        <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: GOLD, marginBottom: 8 }}>New Rate for Extension</div>
                        <div style={{ fontSize: 11, color: MUTED, marginBottom: 8 }}>From {new Date(editForm.pendingRateTransition).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} onwards. Old rate ({c.monthly}) saves to history.</div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <span style={{ color: MUTED, fontSize: 13 }}>£</span>
                          <input type="number" value={editForm.newExtensionRate || ""} onChange={e => setEditForm(f => ({ ...f, newExtensionRate: e.target.value, extension_rate: "£" + e.target.value, monthly: "£" + e.target.value }))} style={{ ...inputStyle, width: 120 }} placeholder="e.g. 130" />
                          <span style={{ fontSize: 11, color: MUTED }}>/ month</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ marginBottom: 20, padding: "16px", background: "#1a1a1a", border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: MUTED, marginBottom: 12 }}>Charges & Expenses</div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Delivery Charge (£)</label>
                      <input type="number" value={editForm.delivery_charge || ""} onChange={e => setEditForm(f => ({ ...f, delivery_charge: e.target.value }))} style={inputStyle} placeholder="0" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle}>Collection Charge (£)</label>
                      <input type="number" value={editForm.collection_charge || ""} onChange={e => setEditForm(f => ({ ...f, collection_charge: e.target.value }))} style={inputStyle} placeholder="0" />
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>Expenses (£)</label>
                    <input type="number" value={editForm.expenses || ""} onChange={e => setEditForm(f => ({ ...f, expenses: e.target.value }))} style={inputStyle} placeholder="0" />
                  </div>
                  <div>
                    <label style={labelStyle}>Expenses Note</label>
                    <input type="text" value={editForm.expenses_note || ""} onChange={e => setEditForm(f => ({ ...f, expenses_note: e.target.value }))} style={inputStyle} placeholder="e.g. delivery £150, collection £120" />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 10, justifyContent: "flex-end" }}>
                  <span style={{ fontSize: 10, color: "#444", letterSpacing: "0.1em", textTransform: "uppercase", alignSelf: "center", marginRight: 4 }}>Payments</span>
                  <button onClick={() => setEditForm(f => ({ ...f, extra_payments: (f.extra_payments || 0) - 1 }))} style={{ background: "none", border: "1px solid #2e2e2e", color: "#555", cursor: "pointer", fontSize: 12, padding: "3px 10px", borderRadius: 2, fontFamily: "Georgia, serif" }}>−</button>
                  <span style={{ fontSize: 11, color: "#555", alignSelf: "center", minWidth: 20, textAlign: "center" }}>{editForm.extra_payments || 0}</span>
                  <button onClick={() => setEditForm(f => ({ ...f, extra_payments: (f.extra_payments || 0) + 1 }))} style={{ background: "none", border: "1px solid #2e2e2e", color: "#555", cursor: "pointer", fontSize: 12, padding: "3px 10px", borderRadius: 2, fontFamily: "Georgia, serif" }}>+</button>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={{ ...styles.btn, flex: 1 }} onClick={saveEdit}>Save Changes</button>
                  <button style={styles.btnGhost} onClick={() => setEditMode(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ padding: 24 }}>
                <div style={{ fontSize: 20, color: TEXT, marginBottom: 4 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: MUTED, marginBottom: 20 }}>{c.business}</div>
                {[
                  ["Customer ID", c.id],
                  ["Type", c.type],
                  ["Location", c.location],
                  ["Machine(s)", c.machine, "machine"],
                  ["Original Contract Term", c.term],
                  ["Original Start Date", c.start_date ? new Date(c.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"],
                  ["Original End Date", c.original_end_date ? new Date(c.original_end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"],
                  ["Monthly Rate", c.monthly],
                  ["Current End Date", getDisplayRenewal(c)],
                  ["Cancel By", (() => { if (!c.renewal) return "—"; const d = new Date(c.renewal); if (isNaN(d)) return "—"; const noticeDays = c.type === "Studio" ? 42 : 21; d.setDate(d.getDate() - noticeDays); return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); })()],
                  ["Status", c.status],
                  ["Delivery Charge", c.delivery_charge ? "£" + parseFloat(c.delivery_charge).toLocaleString() : "—"],
                  ["Collection Charge", c.collection_charge ? "£" + parseFloat(c.collection_charge).toLocaleString() : "—"],
                  ["Expenses", c.expenses ? <span style={{ color: "#8B4A3A" }}>-£{parseFloat(c.expenses).toLocaleString()}{c.expenses_note ? " · " + c.expenses_note : ""}</span> : "—"],
                  ["Total Earned", (() => {
                    if (!c.start_date) return "—";
                    const start = new Date(c.start_date);
                    if (isNaN(start)) return "—";
                    const endRaw = c.status === "Ended" && c.renewal ? new Date(c.renewal) : new Date();
                    const end = new Date(endRaw.getFullYear(), endRaw.getMonth(), endRaw.getDate(), 23, 59, 59);
                    const history = (rateHistories || []).filter(h => h.customer_id === c.id).sort((a, b) => new Date(a.from_date) - new Date(b.from_date));
                    let total = 0; let totalMonths = 0;
                    if (history.length === 0) {
                      const rate = parseFloat((c.monthly || "0").replace(/[£,]/g, ""));
                      let d = new Date(start); d.setMonth(d.getMonth() + 1);
                      while (d <= end) { total += rate; totalMonths++; d.setMonth(d.getMonth() + 1); }
                    } else {
                      const firstFrom = new Date(history[0].from_date);
                      if (firstFrom > start) {
                        const rate = parseFloat((c.monthly || "0").replace(/[£,]/g, ""));
                        let d = new Date(start); d.setMonth(d.getMonth() + 1);
                        while (d <= firstFrom) { total += rate; totalMonths++; d.setMonth(d.getMonth() + 1); }
                      }
                      history.forEach(h => {
                        const pStart = new Date(h.from_date);
                        const pEndRaw = h.to_date ? new Date(h.to_date) : end;
                        const pEnd = pEndRaw < end ? new Date(pEndRaw.getFullYear(), pEndRaw.getMonth(), pEndRaw.getDate(), 23, 59, 59) : end;
                        let d = new Date(pStart); d.setMonth(d.getMonth() + 1);
                        while (d <= pEnd) { total += h.rate; totalMonths++; d.setMonth(d.getMonth() + 1); }
                      });
                    }
                    const extraP = c.extra_payments || 0;
                    const baseRate = parseFloat((c.monthly || "0").replace(/[£,]/g, ""));
                    const extraVal = extraP * baseRate;
                    const deliveryIncome = parseFloat(c.delivery_charge || 0);
                    const collectionIncome = parseFloat(c.collection_charge || 0);
                    const expensesOut = parseFloat(c.expenses || 0);
                    const netTotal = total + extraVal + deliveryIncome + collectionIncome - expensesOut;
                    const totalEarnedText = "£" + Math.round(netTotal).toLocaleString() + " · " + (totalMonths + extraP) + " payment" + ((totalMonths + extraP) !== 1 ? "s" : "");
                    return netTotal < 0 ? <span style={{ color: MAHOGANY }}>{totalEarnedText}</span> : totalEarnedText;
                  })()],
                  c.extension ? ["Extension", (() => {
                    const orig = c.original_end_date ? new Date(c.original_end_date) : null;
                    if (!orig || isNaN(orig)) return c.extension === "Rolling" ? "Rolling monthly" : "+" + c.extension;
                    const endPoint = c.status === "Ended" && c.renewal ? new Date(c.renewal) : new Date();
                    const monthsElapsed = Math.max(0, (endPoint.getFullYear() - orig.getFullYear()) * 12 + (endPoint.getMonth() - orig.getMonth()));
                    if (c.extension === "Rolling") return "Rolling · " + monthsElapsed + " month" + (monthsElapsed !== 1 ? "s" : "");
                    const total = parseInt(c.extension);
                    if (isNaN(total)) return c.extension;
                    return "Extended · " + Math.min(monthsElapsed, total) + " of " + total + " months";
                  })()] : null,
                ].filter(Boolean).map(([k, v, type]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BORDER}`, fontSize: 13 }}>
                    <span style={{ color: MUTED, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{k}</span>
                    <span>{k === "Status" ? <Pill label={v} /> : k === "Monthly Rate" ? <span style={{ color: GOLD }}>{v}</span> : k === "Cancel By" ? <span style={{ color: "#8B3A3A" }}>{v}</span> : type === "machine" && v ? <span>{v.split(",").map(m => m.trim()).map(machineId => <span key={machineId} onClick={() => onSelectMachine(machineId)} style={{ color: "#4CAF82", cursor: "pointer", textDecoration: "underline", marginRight: 10 }}>{machineId} →</span>)}</span> : v}</span>
                  </div>
                ))}
                <CustomerNotesSection customerId={c.id} />
                <div style={{ marginTop: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: MUTED, marginBottom: 10 }}>Files</div>
                  <FileUpload customerId={c.id} />
                </div>
                <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {c.auto_rolled && (
                    <button onClick={async () => {
                      await supabase.from("customers").update({ auto_rolled: false }).eq("id", c.id);
                      setCustomers(prev => prev.map(x => x.id === c.id ? { ...x, auto_rolled: false } : x));
                    }} style={{ ...styles.btnGhost, fontSize: 10 }}>⭐ Dismiss</button>
                  )}
                  {confirmDelete === c.id ? (
                    <>
                      <button onClick={deleteCustomer} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Confirm Delete</button>
                      <button onClick={() => setConfirmDelete(null)} style={styles.btnGhost}>Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(c.id)} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Delete</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ ...styles.card, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
            <p style={{ color: MUTED, fontSize: 12, letterSpacing: "0.1em" }}>Select a customer to view detail</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── MACHINES ────────────────────────────────────────────
function Machines({ customers, machines, setMachines, focusMachineId, clearMachineFocus, setLastAction }) {
  useEffect(() => {
    if (focusMachineId) {
      setSelected(focusMachineId);
      clearMachineFocus();
    }
  }, [focusMachineId]); // eslint-disable-line
  const [filter, setFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("Reformers");
  const [grouped, setGrouped] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const blank = { id: "", model: "Align R8 Pro", type: "Standard", colour: "N/A", customer: "—", location: "", status: "Available", since: "" };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const statuses = ["All", "Deployed", "Available", "Pending", "Maintenance"];

  const getActiveCustomerName = (machineId) => {
    const active = customers.find(c =>
      c.machine && c.machine.split(',').map(x => x.trim()).includes(machineId) &&
      (c.status === "Active" || c.status === "Ending")
    );
    return active ? active.name : null;
  };
  const categoryMachines = categoryFilter === "Chairs" ? machines.filter(m => m.model === "RR Chair") : machines.filter(m => m.model !== "RR Chair");
  const filtered = filter === "All" ? categoryMachines : categoryMachines.filter(m => m.status === filter);

  const parseId = (id) => {
    const cleaned = (id || "").toString().trim();
    const m = cleaned.match(/^([A-Za-z]+)\s*(\d+)/);
    return m ? [m[1].toUpperCase() === "R" ? 0 : 1, parseInt(m[2])] : [2, 0];
  };
  const sortedFiltered = [...filtered].sort((a, b) => {
    const [aL, aN] = parseId(a.id);
    const [bL, bN] = parseId(b.id);
    if (aL !== bL) return aL - bL;
    return aN - bN;
  });

  const buildGroups = () => {
    const groups = [];
    const seen = new Set();
    for (const m of sortedFiltered) {
      if (seen.has(m.id)) continue;
      const customer = m.customer && m.customer !== "—" ? m.customer : null;
      if (customer) {
        const siblings = sortedFiltered.filter(x => x.customer === customer);
        if (siblings.length > 1) {
          const groupKey = siblings.map(x => x.id).join(",");
          if (!seen.has(groupKey)) {
            siblings.forEach(x => seen.add(x.id));
            seen.add(groupKey);
            groups.push({ type: "group", key: groupKey, machines: siblings, customer });
          }
        } else {
          seen.add(m.id);
          groups.push({ type: "single", machine: m });
        }
      } else {
        seen.add(m.id);
        groups.push({ type: "single", machine: m });
      }
    }
    return groups;
  };

  const save = async () => {
    if (!form.id.trim()) return;
    const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    const newRow = { ...form, since: form.since || today };
    const { data, error } = await supabase.from("machines").insert([newRow]).select();
    if (!error && data) setMachines(prev => [...prev, data[0]]);
    setForm(blank); setShowForm(false);
  };

  const saveEdit = async () => {
    const prev = machines.find(x => x.id === selected);
    const { error } = await supabase.from("machines").update(editForm).eq("id", selected);
    if (!error) {
      setLastAction({ type: "edit", table: "machines", prev, id: selected });
      setMachines(prev => prev.map(x => x.id === selected ? { ...x, ...editForm } : x));
    }
    setSelected(null);
  };

  const deleteMachine = async () => {
    const prev = machines.find(x => x.id === selected);
    await supabase.from("machines").delete().eq("id", selected);
    setLastAction({ type: "delete", table: "machines", prev, id: prev.id });
    setMachines(prev => prev.filter(x => x.id !== selected));
    setSelected(null); setConfirm(null);
  };

  const duplicateMachine = () => {
    setForm({
      ...blank,
      model: editForm.model || blank.model,
      colour: editForm.colour || blank.colour,
      type: editForm.type || blank.type,
      location: editForm.location || "",
      purchase_price: editForm.purchase_price || "",
      first_use_date: editForm.first_use_date || "",
      last_service_date: editForm.last_service_date || "",
      next_service_due: editForm.next_service_due || "",
      condition: editForm.condition || "",
    });
    setShowForm(true);
    setSelected(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Fleet · {categoryMachines.length} {categoryFilter === "Chairs" ? "chairs" : "reformers"}</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["Reformers", "Chairs"].map(c => <button key={c} onClick={() => { setCategoryFilter(c); setFilter("All"); }} style={categoryFilter === c ? styles.btn : styles.btnGhost}>{c}</button>)}
          {statuses.map(s => <button key={s} onClick={() => setFilter(s)} style={filter === s ? styles.btn : styles.btnGhost}>{s}</button>)}
          <button onClick={() => setGrouped(g => !g)} style={styles.btnGhost}>{grouped ? "Ungroup" : "Group"}</button>
          <button style={styles.btn} onClick={() => { setForm(f => ({ ...blank, model: categoryFilter === "Chairs" ? "RR Chair" : "Align R8 Pro" })); setShowForm(true); }}>+ Add Machine</button>
        </div>
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>New Machine</span>
            <button style={styles.btnGhost} onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {[["Machine ID (e.g. R10)", "id"], ["Location", "location"], ["Purchase Price (£)", "purchase_price"]].map(([label, key]) => (
              <div key={key} style={fieldWrap}>
                <label style={labelStyle}>{label}</label>
                <input value={form[key] || ""} onChange={e => set(key, e.target.value)} style={inputStyle} />
              </div>
            ))}
            <div style={fieldWrap}>
              <label style={labelStyle}>Model</label>
              <select value={form.model} onChange={e => set("model", e.target.value)} style={selectStyle}>
                <option>Align R8 Pro</option>
                <option>Align C8 Pro</option>
                <option>RR Chair</option>
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Purchase Condition</label>
              <select value={form.purchase_condition || "Brand New"} onChange={e => set("purchase_condition", e.target.value)} style={selectStyle}>
                <option>Brand New</option>
                <option>Second Hand</option>
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Role</label>
              <select value={form.type} onChange={e => set("type", e.target.value)} style={selectStyle}>
                <option>N/A</option>
                <option>Black</option><option>White</option>
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Customer</label>
              <select value={form.customer} onChange={e => set("customer", e.target.value)} style={selectStyle}>
                <option value="—">— Unassigned</option>
                {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={selectStyle}>
                <option>Available</option>
                <option>Deployed</option>
                <option>Pending</option>
                <option>Maintenance</option>
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <button style={{ ...styles.btn, padding: "11px 32px" }} onClick={save}>Save Machine</button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.grid2}>
        <div style={styles.card}>
          <table style={styles.table}>
            <thead><tr>{["ID", "Model", "Customer", "Status"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>
              {grouped ? buildGroups().map(g => {
                if (g.type === "single") {
                  const m = g.machine;
                  return (
                    <tr key={m.id} onClick={() => { setSelected(selected === m.id ? null : m.id); setEditForm({ ...m }); setConfirm(null); }}
                      style={{ cursor: "pointer", background: selected === m.id ? "#2a2a2a" : "transparent" }}>
                      <td style={{ ...styles.td, color: GOLD, fontFamily: "monospace", fontSize: 12 }}>{m.id}</td>
                      <td style={styles.td}>{m.model === "Align R8 Pro" ? "R8" : m.model === "Align C8 Pro" ? "C8" : m.model}</td>
                      <td style={styles.td}><div>{getActiveCustomerName(m.id) || (m.customer && m.customer !== "—" ? <span style={{color: MUTED}}>{m.customer}</span> : <span style={{color: MUTED}}>—</span>)}</div></td>
                      <td style={styles.td}><Pill label={m.status} /></td>
                    </tr>
                  );
                } else {
                  const isOpen = expandedGroups[g.key];
                  return [
                    <tr key={g.key} onClick={() => setExpandedGroups(e => ({ ...e, [g.key]: !e[g.key] }))}
                      style={{ cursor: "pointer", background: "#1e1e1e", borderLeft: `3px solid ${GOLD}` }}>
                      <td style={{ ...styles.td, color: GOLD, fontFamily: "monospace", fontSize: 12 }}>{isOpen ? "▼" : "▶"} {g.machines.map(x => x.id).join(", ")}</td>
                      <td style={styles.td}>{g.machines[0].model === "Align R8 Pro" ? "R8" : g.machines[0].model === "Align C8 Pro" ? "C8" : g.machines[0].model}</td>
                      <td style={{ ...styles.td }}><div style={{color: TEXT}}>{g.customer}</div><div style={{color: MUTED, fontSize: 11}}>{g.machines.length} machines</div></td>
                      <td style={styles.td}><Pill label={g.machines[0].status} /></td>
                    </tr>,
                    isOpen && g.machines.map(m => (
                      <tr key={m.id} onClick={() => { setSelected(selected === m.id ? null : m.id); setEditForm({ ...m }); setConfirm(null); }}
                        style={{ cursor: "pointer", background: selected === m.id ? "#2a2a2a" : "#181818" }}>
                        <td style={{ ...styles.td, color: GOLD, fontFamily: "monospace", fontSize: 12, paddingLeft: 36 }}>{m.id}</td>
                        <td style={styles.td}>{m.model}</td>
                        <td style={{ ...styles.td, color: MUTED, fontSize: 11 }}>{m.colour}</td>
                        <td style={styles.td}><Pill label={m.status} /></td>
                      </tr>
                    ))
                  ];
                }
              }) : sortedFiltered.map(m => (
                <tr key={m.id} onClick={() => { setSelected(selected === m.id ? null : m.id); setEditForm({ ...m }); setConfirm(null); }}
                  style={{ cursor: "pointer", background: selected === m.id ? "#2a2a2a" : "transparent" }}>
                  <td style={{ ...styles.td, color: GOLD, fontFamily: "monospace", fontSize: 12 }}>{m.id}</td>
                  <td style={styles.td}>{m.model === "Align R8 Pro" ? "R8" : m.model === "Align C8 Pro" ? "C8" : m.model}</td>
                  <td style={styles.td}>{getActiveCustomerName(m.id) || (m.customer && m.customer !== "—" ? <span style={{color: MUTED}}>{m.customer}</span> : <span style={{color: MUTED}}>—</span>)}</td>
                  <td style={styles.td}><Pill label={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected ? (
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>Edit · {selected}</span>
              <button style={styles.btnGhost} onClick={() => setSelected(null)}>✕ Close</button>
            </div>
            <div style={{ padding: 24 }}>
              {(() => {
                const machine = machines.find(x => x.id === selected);
                const purchasePrice = parseFloat((machine && machine.purchase_price) || 0);
                const allLinked = customers.filter(x => {
                  if (!x.machine) return false;
                  const machines = x.machine.split(',').map(m => m.trim());
                  return machines.includes(selected);
                });
                const adjustedLinked = allLinked.map(cust => {
                  const custMachineIds = cust.machine ? cust.machine.split(',').map(m => m.trim()) : [];
                  const chairIds = custMachineIds.filter(mid => { const mm = machines.find(x => x.id === mid); return mm && mm.model === "RR Chair"; });
                  const reformerIds = custMachineIds.filter(mid => !chairIds.includes(mid));
                  const chairTotal = parseFloat(cust.chair_monthly || 0) || 0;
                  const totalRate = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
                  const reformerTotal = Math.max(0, totalRate - chairTotal);
                  const isSelectedChair = machine && machine.model === "RR Chair";
                  const machineCount = isSelectedChair ? (chairIds.length || 1) : (reformerIds.length || 1);
                  const splitRate = isSelectedChair ? (chairTotal / machineCount) : (reformerTotal / machineCount);
                  return { ...cust, monthly: "£" + splitRate.toFixed(0), _machineCount: machineCount };
                });
                const today = new Date();
                const isChairView = machine && machine.model === "RR Chair";
                const totalEarned = adjustedLinked.reduce((sum, cust) => {
                  const start = new Date(cust.start_date);
                  if (isNaN(start)) return sum;
                  const endRaw = cust.status === "Ended" && cust.renewal ? new Date(cust.renewal) : today;
                  const end = new Date(endRaw.getFullYear(), endRaw.getMonth(), endRaw.getDate(), 23, 59, 59);
                  const machineCount = cust.machine ? cust.machine.split(',').map(m => m.trim()).length : 1;
                  const history = (window.__rateHistories || []).filter(h => h.customer_id === cust.id).sort((a, b) => new Date(a.from_date) - new Date(b.from_date));
                  if (history.length === 0) {
                    const rate = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
                    let completedMonths = 0;
                    let paymentDate = new Date(start);
                    paymentDate.setMonth(paymentDate.getMonth() + 1);
                    while (paymentDate <= end) { completedMonths++; paymentDate.setMonth(paymentDate.getMonth() + 1); }
                    const baseAmt2 = (completedMonths * rate) + ((cust.extra_payments || 0) * rate);
                    if (isChairView) return sum + baseAmt2;
                    const mcp2 = cust._machineCount || 1;
                    const charges2 = parseFloat(cust.delivery_charge || 0) / mcp2 + parseFloat(cust.collection_charge || 0) / mcp2 - parseFloat(cust.expenses || 0) / mcp2;
                    return sum + baseAmt2 + charges2;
                  }
                  let custTotal = 0;
                  const firstFrom = new Date(history[0].from_date);
                  if (firstFrom > start) {
                    const rate = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
                    let d = new Date(start); d.setMonth(d.getMonth() + 1);
                    while (d <= firstFrom && d <= end) { custTotal += rate / machineCount; d.setMonth(d.getMonth() + 1); }
                  }
                  history.forEach(h => {
                    const pStart = new Date(h.from_date);
                    const pEndRaw = h.to_date ? new Date(h.to_date) : end;
                    const pEnd = pEndRaw < end ? new Date(pEndRaw.getFullYear(), pEndRaw.getMonth(), pEndRaw.getDate(), 23, 59, 59) : end;
                    let d = new Date(pStart); d.setMonth(d.getMonth() + 1);
                    while (d <= pEnd) { custTotal += h.rate / machineCount; d.setMonth(d.getMonth() + 1); }
                  });
                  const extraPay = cust.extra_payments || 0;
                  const baseRatePay = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
                  const mCountPay = cust.machine ? cust.machine.split(",").map(m => m.trim()).length : 1;
                  custTotal += (extraPay * baseRatePay) / mCountPay;
                  if (!isChairView) {
                    custTotal += parseFloat(cust.delivery_charge || 0) / mCountPay;
                    custTotal += parseFloat(cust.collection_charge || 0) / mCountPay;
                    custTotal -= parseFloat(cust.expenses || 0) / mCountPay;
                  }
                  return sum + custTotal;
                }, 0);
                const totalEarnedWithExtra = totalEarned + adjustedLinked.reduce((s, cust) => {
                  const extraPay = cust.extra_payments || 0;
                  const baseRatePay = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
                  const mCountPay = cust.machine ? cust.machine.split(",").map(m => m.trim()).length : 1;
                  return s + (extraPay * baseRatePay) / mCountPay;
                }, 0);
                const pct = purchasePrice > 0 ? Math.min(100, Math.round((totalEarnedWithExtra / purchasePrice) * 100)) : 0;
                const remaining = Math.max(0, purchasePrice - totalEarnedWithExtra);
                const prevCustomers = adjustedLinked.filter(x => x.status === "Ended").sort((a, b) => new Date(b.renewal) - new Date(a.renewal));
                const currentCustomer = adjustedLinked.find(x => x.status === "Active" || x.status === "Ending");
                return (
                  <div style={{ marginBottom: 24 }}>
                    {purchasePrice > 0 && (
                      <div style={{ marginBottom: 20, padding: "16px", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2 }}>
                        <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#888", marginBottom: 10 }}>Payoff Tracker</div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 12 }}>
                          <span style={{ color: "#f0ede8" }}>£{Math.round(totalEarnedWithExtra).toLocaleString()} earned</span>
                          {totalEarnedWithExtra > purchasePrice ? (
                            <span style={{ color: "#4CAF82" }}>£{Math.round(totalEarnedWithExtra - purchasePrice).toLocaleString()} profit earned</span>
                          ) : (
                            <span style={{ color: "#888" }}>£{Math.round(remaining).toLocaleString()} remaining</span>
                          )}
                        </div>
                        <div style={{ background: "#2a2a2a", borderRadius: 2, height: 6, width: "100%" }}>
                          <div style={{ background: pct >= 100 ? "#4CAF82" : "#C9A84C", height: 6, borderRadius: 2, width: pct + "%", transition: "width 0.4s ease" }} />
                        </div>
                        <div style={{ marginTop: 6, fontSize: 11, color: pct >= 100 ? "#4CAF82" : "#888" }}>{pct}% paid off · Purchase price £{purchasePrice.toLocaleString()}</div>
                      </div>
                    )}
                    {currentCustomer && (
                      <div style={{ marginBottom: 12, fontSize: 13 }}>
                        <span style={{ color: "#888", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Current · </span>
                        <span style={{ color: "#4CAF82", cursor: "pointer" }} onClick={() => { if(window.__goToCustomer) window.__goToCustomer(currentCustomer.id); }}>{currentCustomer.name} →</span>
                        <span style={{ color: "#888", fontSize: 11, marginLeft: 8 }}>{currentCustomer.monthly}/mo</span>
                      </div>
                    )}
                    {prevCustomers.length > 0 && (
                      <details style={{ marginBottom: 16 }}>
                        <summary style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#888", cursor: "pointer", marginBottom: 8 }}>Previous Customers · {prevCustomers.length}</summary>
                        {prevCustomers.map(p => (
                          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #2e2e2e", fontSize: 12 }}>
                            <span style={{ color: "#C9A84C", cursor: "pointer" }} onClick={() => { if(window.__goToCustomer) window.__goToCustomer(p.id); }}>{p.name} →</span>
                            <span style={{ color: "#888" }}>{p.monthly}/mo · {p.start_date ? new Date(p.start_date).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : ""}–{p.renewal ? new Date(p.renewal).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : ""}</span>
                          </div>
                        ))}
                      </details>
                    )}
                  </div>
                );
              })()}
              {[["ID", "id"], ["Location", "location"], ["Purchase Price (£)", "purchase_price"]].map(([label, key]) => (
                <div key={key} style={fieldWrap}>
                  <label style={labelStyle}>{label}</label>
                  <input value={editForm[key] || ""} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Use Date</label>
                  <input type="date" value={editForm.first_use_date || ""} onChange={e => setEditForm(f => ({ ...f, first_use_date: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Service Date</label>
                  <input type="date" value={editForm.last_service_date || ""} onChange={e => setEditForm(f => ({ ...f, last_service_date: e.target.value }))} style={inputStyle} />
                </div>
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Next Service Due</label>
                <input type="date" value={editForm.next_service_due || ""} onChange={e => setEditForm(f => ({ ...f, next_service_due: e.target.value }))} style={inputStyle} />
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Condition</label>
                <select value={editForm.condition || "Excellent"} onChange={e => setEditForm(f => ({ ...f, condition: e.target.value }))} style={selectStyle}>
                  {["Excellent", "Good", "Fair", "Needs Attention"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Purchase Condition</label>
                <select value={editForm.purchase_condition || "Brand New"} onChange={e => setEditForm(f => ({ ...f, purchase_condition: e.target.value }))} style={selectStyle}>
                  <option>Brand New</option>
                  <option>Second Hand</option>
                </select>
              </div>
              {[["Model", "model", ["Align R8 Pro", "Align C8 Pro", "RR Chair"]], ["Colour", "colour", ["N/A", "Black", "White"]], ["Status", "status", ["Available", "Deployed", "Pending", "Maintenance"]]].map(([label, key, opts]) => (
                <div key={key} style={fieldWrap}>
                  <label style={labelStyle}>{label}</label>
                  <select value={editForm[key] || opts[0]} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} style={selectStyle}>
                    {opts.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              ))}
              <div style={fieldWrap}>
                <label style={labelStyle}>Customer</label>
                <select value={editForm.customer || "—"} onChange={e => setEditForm(f => ({ ...f, customer: e.target.value }))} style={selectStyle}>
                  <option value="—">— Unassigned</option>
                  {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button style={{ ...styles.btn, flex: 1 }} onClick={saveEdit}>Save Changes</button>
                <button style={styles.btnGhost} onClick={duplicateMachine}>Duplicate</button>
                {confirm === selected ? (
                  <>
                    <button onClick={deleteMachine} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Confirm Delete</button>
                    <button onClick={() => setConfirm(null)} style={styles.btnGhost}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setConfirm(selected)} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Delete</button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ ...styles.card, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
            <p style={{ color: MUTED, fontSize: 12, letterSpacing: "0.1em" }}>Select a machine to edit</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PARTNERS ────────────────────────────────────────────
function Partners({ partners, setPartners }) {
  const [confirm, setConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const blank = { name: "", contact: "", type: "Studio Partner", location: "", machines: "", value: "", status: "Active" };
  const [form, setForm] = useState(blank);

  const save = async () => {
    if (!form.name.trim()) return;
    const { data, error } = await supabase.from("partners").insert([form]).select();
    if (!error && data) setPartners(prev => [...prev, data[0]]);
    setForm(blank); setShowForm(false);
  };

  const remove = async (name) => {
    const prev = partners.find(p => p.name === name);
    await supabase.from("partners").delete().eq("name", name);
    setPartners(p => p.filter(p => p.name !== name));
    if (window.__setLastAction) window.__setLastAction({ type: "delete", table: "partners", prev, restore: (d) => setPartners(p => [...p, d]) });
    setConfirm(null);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Studio & Academy Partners · {partners.length}</p>
        <button style={styles.btn} onClick={() => setShowForm(true)}>+ Add Partner</button>
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>New Partner</span>
            <button style={styles.btnGhost} onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {[["Business Name", "name"], ["Contact Name", "contact"], ["Location", "location"], ["Machines on Hire", "machines"], ["Monthly Value", "value"]].map(([label, key]) => (
              <div key={key} style={fieldWrap}>
                <label style={labelStyle}>{label}</label>
                <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} />
              </div>
            ))}
            <div style={fieldWrap}>
              <label style={labelStyle}>Role</label>
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={selectStyle}>
                {["Studio Partner", "Training Academy", "Home Partner"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <button style={{ ...styles.btn, padding: "11px 32px" }} onClick={save}>Save Partner</button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.grid3}>
        {partners.map(p => (
          <div key={p.name} style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardTitle}>{p.type}</span>
              <Pill label={p.status} />
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ fontSize: 16, color: TEXT, marginBottom: 4 }}>{p.name}</div>
              <div style={{ fontSize: 12, color: MUTED, marginBottom: 20 }}>{p.contact} · {p.location}</div>
              {[["Machines on hire", p.machines], ["Monthly value", p.value]].filter(Boolean).map(([k, v, type]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${BORDER}`, fontSize: 13 }}>
                  <span style={{ color: MUTED, fontSize: 11 }}>{k}</span>
                  <span style={{ color: GOLD }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                {confirm === p.name ? (
                  <>
                    <button onClick={() => remove(p.name)} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Confirm</button>
                    <button onClick={() => setConfirm(null)} style={styles.btnGhost}>Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setConfirm(p.name)} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Delete</button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PAYMENTS ────────────────────────────────────────────
function Payments({ payments, setPayments, customers }) {
  const [showForm, setShowForm] = useState(false);
  const blank = { id: "", customer: "", amount: "", date: "", method: "GoCardless", status: "Collected" };
  const [form, setForm] = useState(blank);

  const save = async () => {
    if (!form.customer || !form.amount) return;
    const amount = form.amount.startsWith("£") ? form.amount : `£${form.amount}`;
    const newRow = { ...form, amount };
    const { data, error } = await supabase.from("payments").insert([newRow]).select();
    if (!error && data) setPayments(prev => [...prev, data[0]]);
    setForm(blank); setShowForm(false);
  };

  const collected = payments.filter(p => p.status === "Collected").reduce((s, p) => s + parseInt((p.amount || "0").replace(/[£,]/g, "")), 0);
  const failed = payments.filter(p => p.status === "Failed");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Payments · GoCardless Direct Debit</p>
        <button style={styles.btn} onClick={() => setShowForm(true)}>+ Add Payment</button>
      </div>

      <div style={styles.grid3}>
        {[
          { label: "Collected", value: `£${collected.toLocaleString()}` },
          { label: "Failed", value: `£${failed.reduce((s, p) => s + parseInt((p.amount || "0").replace(/[£,]/g, "")), 0).toLocaleString()}`, sub: `${failed.length} payment${failed.length !== 1 ? "s" : ""}` },
          { label: "Collection Rate", value: payments.length ? `${Math.round((payments.filter(p => p.status === "Collected").length / payments.length) * 100)}%` : "—" },
        ].map(s => (
          <div key={s.label} style={styles.statCard}>
            <div style={styles.statLabel}>{s.label}</div>
            <div style={styles.statValue}>{s.value}</div>
            {s.sub && <div style={styles.statSub}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {showForm && (
        <div style={{ ...styles.card, marginBottom: 20 }}>
          <div style={styles.cardHeader}>
            <span style={styles.cardTitle}>New Payment</span>
            <button style={styles.btnGhost} onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
          <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>Customer</label>
              <select value={form.customer} onChange={e => setForm(f => ({ ...f, customer: e.target.value }))} style={selectStyle}>
                <option value="">— Select</option>
                {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Amount (£)</label>
              <input value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Method</label>
              <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} style={selectStyle}>
                {["GoCardless", "Bank Transfer", "Cash"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={selectStyle}>
                {["Collected", "Failed", "Pending"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button style={{ ...styles.btn, width: "100%" }} onClick={save}>Save Payment</button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <table style={styles.table}>
          <thead><tr>{["Ref", "Customer", "Amount", "Date", "Method", "Status"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>
            {payments.map((p, i) => (
              <tr key={p.id || i}>
                <td style={{ ...styles.td, color: MUTED, fontFamily: "monospace", fontSize: 11 }}>{p.id}</td>
                <td style={styles.td}>{p.customer}</td>
                <td style={{ ...styles.td, color: GOLD }}>{p.amount}</td>
                <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{p.date}</td>
                <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{p.method}</td>
                <td style={styles.td}><Pill label={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── NOTES ───────────────────────────────────────────────
function Systems({ customers }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const calculateStage = (c) => {
    if (c.status === "Ended") return null;
    if (!c.start_date) return "Enquiry";
    const startDate = new Date(c.start_date);
    const endDate = c.renewal ? new Date(c.renewal) : null;
    const daysSinceStart = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
    const daysUntilEnd = endDate ? Math.floor((endDate - today) / (1000 * 60 * 60 * 24)) : Infinity;
    if (c.status === "Ending") return "Collection";
    if (daysSinceStart < 0) return "Pre-Delivery";
    if (daysSinceStart <= 42) return "First 6 Weeks";
    if (daysUntilEnd <= 30 && daysUntilEnd > 0) return "Pre-Renewal";
    if (c.extension === "Rolling") return "Renewal";
    return "Active Rental";
  };

  const stages = [
    {
      name: "Enquiry & Deposit",
      blurb: "A deposit is a small commitment that makes the bigger one more likely — but only if you don't let the moment go cold.",
      insights: [
        "Reply fast — a slow response before you've even started makes them question what ongoing support looks like",
        "The moment the deposit lands, confirm it straight away — that's what locks their intent in",
        "Keep next steps dead simple — they're already managing a business, don't add to their mental load",
      ],
    },
    {
      name: "Pre-Delivery",
      blurb: "The highest-risk moment for doubt: the money's gone but nothing's arrived yet, so their mind fills the gap with worry.",
      insights: [
        "A quick 'it's on schedule' message closes that open loop before it has time to nag at them",
        "If they've already told staff or clients about the new kit, their reputation's riding on it too — reassurance matters more here",
        "Silence at this stage gets misread as a problem, even when there isn't one",
      ],
    },
    {
      name: "First 6 Weeks",
      blurb: "It's not just the owner adjusting — staff and clients need to build the machine into their routine too, which takes longer in a studio.",
      insights: [
        "Check in around week 2–3 — that's when real friction (space, bookings, staff familiarity) shows up, before anyone complains",
        "Ask what's getting in the way of it being used, not just whether they're happy",
        "Helping them get it embedded now is what determines whether it becomes essential or just sits there",
      ],
    },
    {
      name: "Active Rental",
      blurb: "The real risk here is being forgotten, not being disliked. A busy studio owner's attention is elsewhere by default.",
      insights: [
        "Out of sight becomes out of mind fast when they're running their own business day to day — a check-in isn't a bother, it's what keeps you real to them",
        "One well-timed message beats several routine ones — don't over-contact, just don't vanish",
        "If they seem settled and happy, this is the easiest point to ask for an introduction to another studio",
      ],
    },
    {
      name: "Pre-Renewal",
      blurb: "People default to sticking with what they have — but silence lets the cost feel bigger than the value ever did.",
      insights: [
        "Reach out before the cancel-by date — if they raise it first, the decision's probably already leaning against you",
        "Remind them what it's actually done for their studio, not just that renewal is due",
        "If they hesitate, find out why — budget, space, and 'it just slipped my mind' all need very different responses",
      ],
    },
    {
      name: "Renewal",
      blurb: "People want to feel their decision was the right one — reinforcing that isn't flattery, it lowers the odds they second-guess it later.",
      insights: [
        "A short thank-you costs nothing and quietly locks in that they made a good call",
        "Studio owners talk to other studio owners — this is a natural moment to mention referrals",
      ],
    },
    {
      name: "Collection",
      blurb: "However this goes becomes the story they tell about you — it outweighs the months that came before it.",
      insights: [
        "Make it smooth and easy, no chasing, no awkwardness",
        "Ask why, briefly — it's useful, and it shows you actually cared",
        "A good exit keeps the door open for them to come back, or send someone else your way",
      ],
    },
  ];

  const grouped = {};
  stages.forEach(s => { grouped[s.name] = customers.filter(c => calculateStage(c) === s.name); });

  const StageCard = ({ stage }) => {
    const [open, setOpen] = useState(false);
    const stageCustomers = grouped[stage.name] || [];
    return (
      <div style={{ marginBottom: 10, borderRadius: 4, overflow: "hidden", border: `1px solid ${open ? GOLD + "44" : BORDER}`, transition: "border-color 0.2s" }}>
        <div onClick={() => setOpen(!open)} style={{ padding: "16px 20px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", background: open ? "#1e1e1e" : BG_CARD, borderLeft: `3px solid ${GOLD}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, color: TEXT, fontFamily: "'Cormorant Garant', serif" }}>{stage.name}</span>
            {stageCustomers.length > 0 && (
              <div style={{ display: "flex", gap: 4 }}>
                {stageCustomers.slice(0, 3).map(c => (
                  <span key={c.id} style={{ fontSize: 10, color: "#111", background: GOLD, padding: "1px 8px", borderRadius: 10, fontFamily: "'Cormorant Garant', serif" }}>{c.name.split(" ")[0]}</span>
                ))}
                {stageCustomers.length > 3 && <span style={{ fontSize: 10, color: MUTED }}>+{stageCustomers.length - 3}</span>}
              </div>
            )}
          </div>
          <span style={{ color: GOLD, fontSize: 16, transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
        </div>
        {open && (
          <div style={{ padding: "16px 20px", background: "#181818", borderTop: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 12, color: MUTED, fontStyle: "italic", marginBottom: 14 }}>{stage.blurb}</div>
            {stageCustomers.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {stageCustomers.map(c => (
                  <span key={c.id} style={{ fontSize: 11, color: GOLD, background: GOLD + "15", padding: "3px 10px", borderRadius: 2, border: `1px solid ${GOLD}33` }}>{c.name}</span>
                ))}
              </div>
            )}
            {stage.insights.map((ins, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: GOLD, marginTop: 7, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: TEXT, lineHeight: 1.6 }}>{ins}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 780 }}>
      <p style={styles.sectionTitle}>Customer Lifecycle</p>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 28 }}>Where each customer is right now, and what to think about at that stage.</div>
      {stages.map(s => <StageCard key={s.name} stage={s} />)}
    </div>
  );
}

// ── ENQUIRIES ───────────────────────────────────────────
function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [step, setStep] = useState(null); // null | 'type' | 'source' | 'website' | 'other'
  const [pending, setPending] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [waitlistForm, setWaitlistForm] = useState(null);
  const [waitlistData, setWaitlistData] = useState({});
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [reasonForm, setReasonForm] = useState(null);
  const [reasonData, setReasonData] = useState({});
  const [typeFilter, setTypeFilter] = useState("All");

  const LOST_REASONS = ["Went quiet / no response", "No response after pricing mentioned", "Didn't get back to initial message", "Too far / outside zone", "No reformers available", "Other"];

  useEffect(() => {
    supabase.from("enquiries").select("*").order("created_at", { ascending: false }).then(({ data }) => {
      if (data) setEnquiries(data);
    });
  }, []);

  const addEnquiry = async (entry) => {
    const now = entry.custom_date ? new Date(entry.custom_date) : new Date();
    const row = {
      ...entry,
      created_at: now.toISOString(),
      month: now.toLocaleDateString("en-GB", { month: "long", year: "numeric" }),
      month_key: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    };
    delete row.custom_date;
    const { data } = await supabase.from("enquiries").insert([row]).select().single();
    if (data) setEnquiries(prev => [data, ...prev]);
    setStep(null);
    setPending({});
  };

  const remove = async (id) => {
    const prev = enquiries.find(e => e.id === id);
    await supabase.from("enquiries").delete().eq("id", id);
    setEnquiries(p => p.filter(e => e.id !== id));
    if (window.__setLastAction) window.__setLastAction({
      type: "delete",
      table: "enquiries",
      prev,
      id: prev.id,
    });
  };

  const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;

  const grouped = enquiries.filter(e => typeFilter === "All" || e.type === typeFilter).reduce((acc, e) => {
    const key = e.month_key || "unknown";
    if (!acc[key]) acc[key] = { label: e.month, entries: [] };
    acc[key].entries.push(e);
    return acc;
  }, {});

  const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  const sourceLabel = (e) => {
    if (e.source === "Website" && e.website_source) return `Website · ${e.website_source}`;
    if (e.source === "Other" && e.note) return `Other · ${e.note}`;
    return e.source;
  };

  const btnBase = { background: "none", border: `1px solid ${BORDER}`, borderRadius: 2, padding: "10px 18px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", fontFamily: "'Cormorant Garant', serif", color: TEXT, transition: "all 0.15s" };
  const btnActive = { ...btnBase, background: GOLD + "22", borderColor: GOLD, color: GOLD };

  return (
    <div>
      <p style={styles.sectionTitle}>Enquiries</p>

      {/* WAITLIST SECTION */}
      <div style={{ ...styles.card, marginBottom: 24 }}>
        <div style={{ ...styles.cardHeader, cursor: "pointer" }} onClick={() => setShowWaitlist(w => !w)}>
          <span style={{ ...styles.cardTitle, color: "#8B4A3A" }}>Waitlist · {enquiries.filter(e => e.turned_away && e.waitlist_name).length}</span>
          <span style={{ color: "#888", fontSize: 14 }}>{showWaitlist ? "▲" : "▼"}</span>
        </div>
        {showWaitlist && (
          <div>
            {enquiries.filter(e => e.turned_away && e.waitlist_name).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).length === 0 && (
              <div style={{ padding: "20px 24px", fontSize: 12, color: "#888" }}>No one on the waitlist yet</div>
            )}
            {enquiries.filter(e => e.turned_away && e.waitlist_name).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(e => (
              <div key={e.id} style={{ padding: "16px 24px", borderBottom: "1px solid #1f1f1f", display: "flex", gap: 16, alignItems: "flex-start" }}>
                <div style={{ width: 3, minHeight: 40, background: "#8B4A3A", borderRadius: 2, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 14, color: "#f0ede8" }}>{e.waitlist_name}</span>
                    {e.waitlist_phone && <span style={{ fontSize: 12, color: "#f0ede8" }}>{e.waitlist_phone}</span>}
                    <span style={{ fontSize: 10, color: "#888", marginLeft: "auto" }}>{new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 2, color: e.type === "Home" ? "#5B9BD5" : "#E05656", background: (e.type === "Home" ? "#5B9BD5" : "#E05656") + "22", border: `1px solid ${(e.type === "Home" ? "#5B9BD5" : "#E05656")}55` }}>{e.type}</span>
                    {e.source && <span style={{ fontSize: 11, color: "#888" }}>{e.source}</span>}
                    {e.waitlist_location && <span style={{ fontSize: 11, color: "#888" }}>· {e.waitlist_location}</span>}
                  </div>
                  {(e.waitlist_wanted_when || e.waitlist_wanted_what) && (
                    <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                      {e.waitlist_wanted_when && <span style={{ fontSize: 11 }}><span style={{ color: "#f0ede8" }}>When: </span><span style={{ color: "#C9A84C" }}>{e.waitlist_wanted_when}</span></span>}
                      {e.waitlist_wanted_what && <span style={{ fontSize: 11 }}><span style={{ color: "#f0ede8" }}>What: </span><span style={{ color: "#C9A84C" }}>{e.waitlist_wanted_what}</span></span>}
                    </div>
                  )}
                  {e.waitlist_note && <div style={{ fontSize: 12, color: "#aaa", lineHeight: 1.5, marginTop: 4 }}>{e.waitlist_note}</div>}
                </div>
                <button onClick={async () => {
                  await supabase.from("enquiries").update({ turned_away: false, waitlist_name: null, waitlist_phone: null, waitlist_note: null }).eq("id", e.id);
                  setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, turned_away: false, waitlist_name: null, waitlist_phone: null, waitlist_note: null } : x));
                }} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 13 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* LOGGER */}
      <div style={{ ...styles.card, marginBottom: 24, padding: 24 }}>
        <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: MUTED, marginBottom: 16 }}>
          {!step && "Log new enquiry"}
          {step === "type" && "Step 1 · Type"}
          {step === "zone" && "Step 2 · Delivery Zone"}
          {step === "source" && "Step 3 · Source"}
          {step === "website" && "Step 4 · Website source"}
          {step === "other" && "Step 3 · Other — add note"}
        </div>

        {!step && (
          <button style={styles.btn} onClick={() => setStep("type")}>+ Log Enquiry</button>
        )}

        {step === "type" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {["Home", "Studio"].map(t => (
              <button key={t} style={pending.type === t ? btnActive : btnBase}
                onClick={() => { setPending(p => ({ ...p, type: t })); setStep("zone"); }}>
                {t}
              </button>
            ))}
            <button style={{ ...btnBase, color: MUTED }} onClick={() => { setStep(null); setPending({}); }}>✕ Cancel</button>
          </div>
        )}

        {step === "zone" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span></div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[
                { label: "Within Zone", value: "Within Zone" },
                { label: "Outside Zone (2.45hr-4hr)", value: "Outside Zone (2.45hr-4hr)" },
                { label: "Outside Zone (4hr+)", value: "Outside Zone (4hr+)" },
              ].map(z => (
                <button key={z.value} style={pending.zone === z.value ? btnActive : btnBase}
                  onClick={() => { setPending(p => ({ ...p, zone: z.value })); setStep("region"); }}>
                  {z.label}
                </button>
              ))}
              <button style={{ ...btnBase, color: MUTED }} onClick={() => setStep("type")}>← Back</button>
            </div>
          </div>
        )}

        {step === "region" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span> · Zone: <span style={{ color: GOLD }}>{pending.zone}</span></div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["North West", "North East", "Yorkshire", "Midlands", "London", "South East", "South West", "Wales", "Scotland", "Other"].map(r => (
                <button key={r} style={pending.region === r ? btnActive : btnBase}
                  onClick={() => { setPending(p => ({ ...p, region: r })); setStep("source"); }}>
                  {r}
                </button>
              ))}
              <button style={{ ...btnBase, color: MUTED }} onClick={() => setStep("zone")}>← Back</button>
            </div>
          </div>
        )}

        {step === "source" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span> · Zone: <span style={{ color: GOLD }}>{pending.zone}</span></div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["Instagram", "TikTok", "SMS / Email"].map(s => (
                <button key={s} style={btnBase}
                  onClick={() => { setPending(p => ({ ...p, source: s })); setStep("notes"); }}>
                  {s}
                </button>
              ))}
              <button style={btnBase} onClick={() => { setPending(p => ({ ...p, source: "Website" })); setStep("website"); }}>Website</button>
              <button style={btnBase} onClick={() => { setPending(p => ({ ...p, source: "Other" })); setStep("other"); }}>Other</button>
              <button style={{ ...btnBase, color: MUTED }} onClick={() => setStep("region")}>← Back</button>
            </div>
          </div>
        )}

        {step === "website" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span> · Source: <span style={{ color: GOLD }}>Website</span></div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["Instagram", "Google/Web", "TikTok", "Friend/Family", "Other"].map(s => (
                <button key={s} style={btnBase}
                  onClick={() => { setPending(p => ({ ...p, source: "Website", website_source: s })); setStep("notes"); }}>
                  {s}
                </button>
              ))}
              <button style={{ ...btnBase, color: MUTED }} onClick={() => setStep("source")}>← Back</button>
            </div>
          </div>
        )}

        {step === "notes" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span> · Source: <span style={{ color: GOLD }}>{pending.source}{pending.website_source ? " · " + pending.website_source : ""}</span></div>
            <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
              <input placeholder="Name (optional)" style={{ ...inputStyle, width: 180, flex: "none" }}
                value={pending.name || ""}
                onChange={e => setPending(p => ({ ...p, name: e.target.value }))} />
              <input placeholder="Interesting notes... (optional)" style={{ ...inputStyle, flex: 1 }}
                value={pending.interesting_note || ""}
                onChange={e => setPending(p => ({ ...p, interesting_note: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && addEnquiry({ ...pending })} />
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ ...labelStyle, marginBottom: 0, whiteSpace: "nowrap" }}>Date</label>
                <input type="date" value={pending.custom_date || new Date().toISOString().split("T")[0]} onChange={e => setPending(p => ({ ...p, custom_date: e.target.value }))} style={{ ...inputStyle, width: 160 }} />
              </div>
              <button style={styles.btn} onClick={() => addEnquiry({ ...pending })}>Save</button>
              <button style={styles.btnGhost} onClick={() => setStep(pending.source === "Website" ? "website" : "source")}>← Back</button>
            </div>
          </div>
        )}

        {step === "other" && (
          <div>
            <div style={{ fontSize: 11, color: MUTED, marginBottom: 12 }}>Type: <span style={{ color: GOLD }}>{pending.type}</span> · Source: <span style={{ color: GOLD }}>Other</span></div>
            <div style={{ display: "flex", gap: 10 }}>
              <input autoFocus placeholder="e.g. Referral from studio..." style={{ ...inputStyle, flex: 1 }}
                value={pending.note || ""}
                onChange={e => setPending(p => ({ ...p, note: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && pending.note && addEnquiry({ ...pending })} />
              <button style={styles.btn} onClick={() => pending.note && addEnquiry({ ...pending })}>Add</button>
              <button style={styles.btnGhost} onClick={() => setStep("source")}>← Back</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["All", "Studio", "Home"].map(t => (
          <button key={t} onClick={() => setTypeFilter(t)} style={typeFilter === t ? styles.btn : styles.btnGhost}>{t}</button>
        ))}
      </div>

      {/* MONTHLY BREAKDOWN */}
      {sortedKeys.map(key => {
        const { label, entries } = grouped[key];
        const isCurrent = key === currentMonthKey;
        const isOpen = isCurrent || expandedMonths[key];

        const typeCounts = entries.reduce((a, e) => { a[e.type] = (a[e.type] || 0) + 1; return a; }, {});
        const sourceCounts = entries.reduce((a, e) => {
          const s = sourceLabel(e);
          a[s] = (a[s] || 0) + 1;
          return a;
        }, {});
        const regionCounts = entries.reduce((a, e) => {
          if (e.region) { a[e.region] = (a[e.region] || 0) + 1; }
          return a;
        }, {});
        const convertedCount = entries.filter(e => e.converted).length;

        return (
          <div key={key} style={{ ...styles.card, marginBottom: 12 }}>
            <div style={{ ...styles.cardHeader, cursor: "pointer" }}
              onClick={() => setExpandedMonths(p => ({ ...p, [key]: !p[key] }))}>
              <span style={{ ...styles.cardTitle, color: isCurrent ? GOLD : MUTED }}>
                {label || key} · {entries.length}
              </span>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                {Object.entries(typeCounts).map(([t, n]) => (
                  <span key={t} style={{ fontSize: 11, color: MUTED }}>{t}: {n}</span>
                ))}
                {entries.filter(e => e.interesting_note).length > 0 && (
                  <span style={{ fontSize: 11, color: MUTED }}>📝 {entries.filter(e => e.interesting_note).length}</span>
                )}
                {convertedCount > 0 && (
                  <span style={{ fontSize: 11, color: "#4CAF82" }}>✓ {convertedCount} converted</span>
                )}
                {entries.filter(e => e.turned_away).length > 0 && (
                  <span style={{ fontSize: 11, color: "#8B4A3A" }}>W {entries.filter(e => e.turned_away).length} waitlisted</span>
                )}
                <span style={{ color: MUTED, fontSize: 14 }}>{isOpen ? "▲" : "▼"}</span>
              </div>
            </div>

            {isOpen && (
              <div>
                {/* Source summary */}
                <div style={{ padding: "12px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {Object.entries(sourceCounts).map(([s, n]) => (
                    <span key={s} style={{ fontSize: 11, color: MUTED }}>{s}: <span style={{ color: TEXT }}>{n}</span></span>
                  ))}
                </div>
                {Object.keys(regionCounts).length > 0 && (
                  <div style={{ padding: "10px 24px", borderBottom: `1px solid ${BORDER}`, display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {Object.entries(regionCounts).sort((a,b) => b[1]-a[1]).map(([r, n]) => (
                      <span key={r} style={{ fontSize: 11, color: MUTED }}>{r}: <span style={{ color: GOLD }}>{n}</span></span>
                    ))}
                  </div>
                )}
                {/* Individual entries */}
                {entries.map(e => (
                  <div key={e.id} style={{ padding: "12px 24px", borderBottom: `1px solid #1f1f1f`, display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ ...styles.pill(e.type === "Studio" ? "#4CAF82" : GOLD), fontSize: 10 }}>{e.type}</span>
                    <div style={{ flex: 1 }}>
                      {e.name && <span style={{ fontSize: 13, color: GOLD, marginRight: 8 }}>{e.name}</span>}
                      <span style={{ fontSize: 13, color: TEXT }}>{sourceLabel(e)}</span>
                      {e.zone === "Outside Zone (2.45hr-4hr)" && <span style={{ fontSize: 11, color: MAHOGANY, marginLeft: 8 }}>2.45hr+</span>}
                      {e.zone === "Outside Zone (4hr+)" && <span style={{ fontSize: 11, color: MAHOGANY, marginLeft: 8 }}>4hr+</span>}
                      {e.region && <span style={{ fontSize: 11, color: MUTED, marginLeft: 8 }}>· {e.region}</span>}
                      {e.interesting_note && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{e.interesting_note}</div>}
                    </div>
                    <span style={{ fontSize: 11, color: MUTED }}>{new Date(e.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
                    <div onClick={async () => { const next = !e.converted; await supabase.from("enquiries").update({ converted: next, turned_away: next ? false : e.turned_away }).eq("id", e.id); setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, converted: next, turned_away: next ? false : x.turned_away } : x)); }} style={{ width: 20, height: 20, borderRadius: 2, border: `1px solid ${e.converted ? "#4CAF82" : BORDER}`, background: e.converted ? "#4CAF82" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, title: "Converted" }}>
                      {e.converted && <span style={{ color: "#111", fontSize: 11, fontWeight: "bold" }}>✓</span>}
                    </div>
                    <div onClick={() => setWaitlistForm(e.turned_away ? null : e.id)} style={{ width: 20, height: 20, borderRadius: 2, border: `1px solid ${e.turned_away ? "#8B4A3A" : BORDER}`, background: e.turned_away ? "#8B4A3A" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {e.turned_away && <span style={{ color: "#fff", fontSize: 11, fontWeight: "bold" }}>W</span>}
                    </div>
                    {e.type === "Studio" && (() => {
                      const val = e.msg1_reply;
                      const cycle = { null: "yes", undefined: "yes", yes: "no", no: null };
                      const color = val === "yes" ? "#4CAF82" : val === "no" ? MAHOGANY : MUTED;
                      const label = val === "yes" ? "Y" : val === "no" ? "N" : "1";
                      return (
                        <>
                          <button title={`Message 1 reply${val ? " · " + val.toUpperCase() : ""}`} onClick={async () => {
                            const next = cycle[val] !== undefined ? cycle[val] : cycle[null];
                            await supabase.from("enquiries").update({ msg1_reply: next }).eq("id", e.id);
                            setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, msg1_reply: next } : x));
                          }} style={{ background: val ? color + "33" : "none", border: `1px solid ${val ? color : BORDER}`, color, cursor: "pointer", fontSize: 11, width: 24, padding: "2px 0", borderRadius: 2, flexShrink: 0, fontFamily: "'Cormorant Garant', serif" }}>{label}</button>
                          <select
                            title="Term asked (months)"
                            value={e.term_requested || ""}
                            onChange={async ev => {
                              const v = ev.target.value;
                              setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, term_requested: v } : x));
                              await supabase.from("enquiries").update({ term_requested: v }).eq("id", e.id);
                            }}
                            style={{ appearance: "none", WebkitAppearance: "none", MozAppearance: "none", background: e.term_requested ? GOLD + "22" : "none", border: `1px solid ${e.term_requested ? GOLD : BORDER}`, color: e.term_requested ? GOLD : MUTED, fontSize: 11, width: 38, height: 24, padding: 0, textAlign: "center", textAlignLast: "center", borderRadius: 2, cursor: "pointer", flexShrink: 0, fontFamily: "'Cormorant Garant', serif", outline: "none" }}
                          >
                            <option value="">—</option>
                            {["4", "6", "8", "10", "12", "14"].map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </>
                      );
                    })()}
                    {reasonForm === e.id && (
                      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000aa", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setReasonForm(null)}>
                        <div style={{ background: "#242424", border: "1px solid #2e2e2e", borderRadius: 4, padding: 32, minWidth: 360 }} onClick={ev => ev.stopPropagation()}>
                          <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: MAHOGANY, marginBottom: 20 }}>Why didn't this move forward?</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                            {LOST_REASONS.map(r => (
                              <button key={r} onClick={() => setReasonData(d => ({ ...d, reason: r }))} style={{ background: reasonData.reason === r ? MAHOGANY + "22" : "none", border: `1px solid ${reasonData.reason === r ? MAHOGANY : BORDER}`, color: reasonData.reason === r ? MAHOGANY : TEXT, padding: "7px 14px", fontSize: 11, letterSpacing: "0.05em", cursor: "pointer", borderRadius: 2, fontFamily: "'Cormorant Garant', serif" }}>{r}</button>
                            ))}
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: MUTED, display: "block", marginBottom: 6 }}>Additional note (optional)</label>
                            <textarea value={reasonData.note || ""} onChange={ev => setReasonData(d => ({ ...d, note: ev.target.value }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                          </div>
                          <div style={{ display: "flex", gap: 10 }}>
                            <button onClick={async () => {
                              await supabase.from("enquiries").update({ lost_reason: reasonData.reason || null, lost_reason_note: reasonData.note || null }).eq("id", e.id);
                              setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, lost_reason: reasonData.reason || null, lost_reason_note: reasonData.note || null } : x));
                              setReasonForm(null); setReasonData({});
                            }} style={styles.btn}>Save</button>
                            {e.lost_reason && (
                              <button onClick={async () => {
                                await supabase.from("enquiries").update({ lost_reason: null, lost_reason_note: null }).eq("id", e.id);
                                setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, lost_reason: null, lost_reason_note: null } : x));
                                setReasonForm(null); setReasonData({});
                              }} style={{ ...styles.btnGhost, color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Clear</button>
                            )}
                            <button onClick={() => { setReasonForm(null); setReasonData({}); }} style={styles.btnGhost}>Cancel</button>
                          </div>
                        </div>
                      </div>
                    )}
                    {waitlistForm === e.id && (
                      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "#000000aa", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setWaitlistForm(null)}>
                        <div style={{ background: "#242424", border: "1px solid #2e2e2e", borderRadius: 4, padding: 32, minWidth: 340 }} onClick={ev => ev.stopPropagation()}>
                          <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#C9A84C", marginBottom: 20 }}>Add to Waitlist</div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Name</label>
                            <input autoFocus value={waitlistData.name || ""} onChange={ev => setWaitlistData(d => ({ ...d, name: ev.target.value }))} style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Phone / Social</label>
                            <input value={waitlistData.phone || ""} onChange={ev => setWaitlistData(d => ({ ...d, phone: ev.target.value }))} style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Location</label>
                            <input value={waitlistData.location || ""} onChange={ev => setWaitlistData(d => ({ ...d, location: ev.target.value }))} style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>When They Want It</label>
                            <input value={waitlistData.wanted_when || ""} onChange={ev => setWaitlistData(d => ({ ...d, wanted_when: ev.target.value }))} placeholder="" style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>What They Want</label>
                            <input value={waitlistData.wanted_what || ""} onChange={ev => setWaitlistData(d => ({ ...d, wanted_what: ev.target.value }))} placeholder="" style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box" }} />
                          </div>
                          <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#888", display: "block", marginBottom: 6 }}>Notes</label>
                            <textarea value={waitlistData.note || ""} onChange={ev => setWaitlistData(d => ({ ...d, note: ev.target.value }))} rows={3} style={{ width: "100%", background: "#1a1a1a", border: "1px solid #2e2e2e", borderRadius: 2, padding: "9px 12px", color: "#f0ede8", fontSize: 13, fontFamily: "Inter, sans-serif", outline: "none", boxSizing: "border-box", resize: "vertical" }} />
                          </div>
                          <div style={{ display: "flex", gap: 10 }}>
                            <button onClick={async () => {
                              await supabase.from("enquiries").update({ turned_away: true, converted: false, waitlist_name: waitlistData.name, waitlist_phone: waitlistData.phone, waitlist_note: waitlistData.note, waitlist_wanted_when: waitlistData.wanted_when, waitlist_wanted_what: waitlistData.wanted_what, waitlist_location: waitlistData.location }).eq("id", e.id);
                              setEnquiries(prev => prev.map(x => x.id === e.id ? { ...x, turned_away: true, converted: false, waitlist_name: waitlistData.name, waitlist_phone: waitlistData.phone, waitlist_note: waitlistData.note, waitlist_wanted_when: waitlistData.wanted_when, waitlist_wanted_what: waitlistData.wanted_what, waitlist_location: waitlistData.location } : x));
                              setWaitlistForm(null); setWaitlistData({});
                            }} style={{ background: "#C9A84C", color: "#111", border: "none", padding: "9px 20px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2, fontFamily: "Inter, sans-serif", flex: 1 }}>Save</button>
                            <button onClick={() => { setWaitlistForm(null); setWaitlistData({}); }} style={{ background: "none", color: "#888", border: "1px solid #2e2e2e", padding: "9px 16px", fontSize: 11, cursor: "pointer", borderRadius: 2, fontFamily: "Inter, sans-serif" }}>Cancel</button>
                          </div>
                        </div>
                      </div>
                    )}
                    <button onClick={() => remove(e.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── APP ─────────────────────────────────────────────────



function GrowthNotes({ important, setImportant, ideas, setIdeas }) {
  const ListSection = ({ title, color, items, setItems, type }) => {
    const [input, setInput] = useState("");
    const add = async () => {
      if (!input.trim()) return;
      const { data } = await supabase.from("notes").insert({ type, text: input.trim() }).select().single();
      if (data) setItems(prev => [...prev, { id: data.id, text: data.text }]);
      setInput("");
    };
    const remove = async (id) => {
      await supabase.from("notes").delete().eq("id", id);
      setItems(p => p.filter(i => i.id !== id));
    };
    return (
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={{ ...styles.cardTitle, color }}>{title}</span>
          <span style={{ fontSize: 10, color: MUTED }}>{items.length} items</span>
        </div>
        <div>
          {items.map(item => (
            <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 24px", borderBottom: `1px solid ${BORDER}` }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, marginTop: 5, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>{item.text}</span>
              <button onClick={() => remove(item.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 14 }}>✕</button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, padding: "16px 24px" }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && add()}
              placeholder="Add item..." style={{ flex: 1, background: "#1a1a1a", border: `1px solid ${BORDER}`, borderRadius: 2, padding: "9px 14px", color: TEXT, fontSize: 13, fontFamily: "'Cormorant Garant', serif", outline: "none" }} />
            <button onClick={add} style={{ ...styles.btn, background: color }}>Add</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
      <ListSection title="⚠ Important" color={MAHOGANY} items={important} setItems={setImportant} type="important" />
      <ListSection title="💡 Ideas" color={GOLD} items={ideas} setItems={setIdeas} type="idea" />
    </div>
  );
}

function DailyGoals() {
  const [goals, setGoals] = useState([]);
  const [input, setInput] = useState("");

  useEffect(() => {
    supabase.from("daily_goals").select("*").order("created_at", { ascending: true }).then(({ data }) => {
      if (data) setGoals(data);
    });
  }, []);

  const add = async () => {
    if (!input.trim()) return;
    const { data } = await supabase.from("daily_goals").insert({ text: input.trim(), done: false }).select().single();
    if (data) setGoals(prev => [...prev, data]);
    setInput("");
  };

  const toggle = async (id, done) => {
    await supabase.from("daily_goals").update({ done: !done }).eq("id", id);
    setGoals(prev => prev.map(g => g.id === id ? { ...g, done: !done } : g));
  };

  const remove = async (id) => {
    await supabase.from("daily_goals").delete().eq("id", id);
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  const clearAll = async () => {
    await supabase.from("daily_goals").delete().neq("id", "00000000-0000-0000-0000-000000000000");
    setGoals([]);
  };

  const done = goals.filter(g => g.done).length;
  const total = goals.length;

  return (
    <div style={{ ...styles.card, marginBottom: 24 }}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>Today's Goals {total > 0 ? `· ${done}/${total}` : ""}</span>
        {total > 0 && <button onClick={clearAll} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px", color: MAHOGANY, borderColor: MAHOGANY + "44" }}>Clear All</button>}
      </div>
      <div>
        {goals.map(g => (
          <div key={g.id} style={{ padding: "12px 24px", borderBottom: `1px solid #1f1f1f`, display: "flex", alignItems: "center", gap: 14 }}>
            <div onClick={() => toggle(g.id, g.done)} style={{ width: 18, height: 18, borderRadius: 2, border: `1px solid ${g.done ? "#4CAF82" : BORDER}`, background: g.done ? "#4CAF82" : "transparent", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {g.done && <span style={{ color: "#111", fontSize: 11, fontWeight: "bold" }}>✓</span>}
            </div>
            <span style={{ flex: 1, fontSize: 13, color: g.done ? MUTED : TEXT, textDecoration: g.done ? "line-through" : "none", lineHeight: 1.5 }}>{g.text}</span>
            <button onClick={() => remove(g.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
          </div>
        ))}
        <div style={{ padding: "12px 24px", display: "flex", gap: 10 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Add a goal..."
            style={{ ...inputStyle, flex: 1 }}
          />
          <button onClick={add} style={styles.btnGhost}>Add</button>
        </div>
      </div>
    </div>
  );
}

function BusinessBacklog() {
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    supabase.from("business_backlog").select("*").order("created_at", { ascending: true }).then(({ data }) => {
      if (data) setItems(data);
    });
  }, []);

  const add = async () => {
    if (!input.trim()) return;
    const { data } = await supabase.from("business_backlog").insert({ text: input.trim(), done: false }).select().single();
    if (data) setItems(prev => [...prev, data]);
    setInput("");
  };

  const toggle = async (id, done) => {
    await supabase.from("business_backlog").update({ done: !done }).eq("id", id);
    setItems(prev => prev.map(g => g.id === id ? { ...g, done: !done } : g));
  };

  const remove = async (id) => {
    await supabase.from("business_backlog").delete().eq("id", id);
    setItems(prev => prev.filter(g => g.id !== id));
  };

  const pending = items.filter(i => !i.done);
  const done = items.filter(i => i.done);

  return (
    <div style={{ ...styles.card, marginBottom: 24 }}>
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>Business Backlog</span>
        <span style={{ fontSize: 10, color: MUTED }}>{pending.length} to do · {done.length} done</span>
      </div>
      <div style={{ fontSize: 11, color: MUTED, padding: "12px 24px 0" }}>The boring admin stuff — pick one off when you've got a spare half hour.</div>
      <div>
        {pending.map(g => (
          <div key={g.id} style={{ padding: "12px 24px", borderBottom: "1px solid #1f1f1f", display: "flex", alignItems: "center", gap: 14 }}>
            <div onClick={() => toggle(g.id, g.done)} style={{ width: 18, height: 18, borderRadius: 2, border: `1px solid ${BORDER}`, cursor: "pointer", flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 13, color: TEXT, lineHeight: 1.5 }}>{g.text}</span>
            <button onClick={() => remove(g.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
          </div>
        ))}
        {pending.length === 0 && <div style={{ padding: "20px 24px", fontSize: 12, color: MUTED }}>Nothing left — add something for next time you're bored.</div>}
        <div style={{ padding: "12px 24px", display: "flex", gap: 10 }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="Add a backlog item..." style={{ ...inputStyle, flex: 1 }} />
          <button onClick={add} style={styles.btnGhost}>Add</button>
        </div>
        {done.length > 0 && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            <div onClick={() => setShowDone(s => !s)} style={{ padding: "10px 24px", cursor: "pointer", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#4CAF82" }}>
              ✓ Completed · {done.length} {showDone ? "▲" : "▼"}
            </div>
            {showDone && done.map(g => (
              <div key={g.id} style={{ padding: "10px 24px", borderTop: "1px solid #1f1f1f", display: "flex", alignItems: "center", gap: 14, opacity: 0.6 }}>
                <div onClick={() => toggle(g.id, g.done)} style={{ width: 18, height: 18, borderRadius: 2, border: "1px solid #4CAF82", background: "#4CAF82", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ color: "#111", fontSize: 11, fontWeight: "bold" }}>✓</span>
                </div>
                <span style={{ flex: 1, fontSize: 13, color: MUTED, textDecoration: "line-through", lineHeight: 1.5 }}>{g.text}</span>
                <button onClick={() => remove(g.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Growth({ important, setImportant, ideas, setIdeas }) {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) return <div style={{ color: MUTED, fontSize: 12, letterSpacing: "0.2em" }}>Loading...</div>;

  return (
    <div>
      <DailyGoals />
      <BusinessBacklog />
      <GrowthNotes important={important} setImportant={setImportant} ideas={ideas} setIdeas={setIdeas} />
    </div>
  );
}



function PinLock({ onUnlock }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);
  const PIN = "1527";

  const handleKey = (e) => {
    if (e.key === "Enter") checkPin();
  };

  const checkPin = () => {
    if (input === PIN) {
      localStorage.setItem("crm_unlocked", Date.now().toString());
      onUnlock();
    } else {
      setError(true);
      setInput("");
      setTimeout(() => setError(false), 1500);
    }
  };

  return (
    <div style={{ background: "#1a1a1a", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 24 }}>
      <div style={{ fontFamily: "Georgia, serif", fontSize: 18, color: "#C9A84C", letterSpacing: "0.2em", textTransform: "uppercase" }}>ReformRentals</div>
      <div style={{ background: "#242424", border: "1px solid #2e2e2e", borderRadius: 4, padding: "40px 48px", display: "flex", flexDirection: "column", alignItems: "center", gap: 20, minWidth: 280 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#888" }}>Enter PIN</div>
        <input
          autoFocus
          type="password"
          inputMode="numeric"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          maxLength={6}
          style={{ width: "100%", background: "#1a1a1a", border: `1px solid ${error ? "#8B4A3A" : "#2e2e2e"}`, borderRadius: 2, padding: "12px 16px", color: "#f0ede8", fontSize: 20, fontFamily: "Georgia, serif", outline: "none", textAlign: "center", letterSpacing: "0.3em", boxSizing: "border-box", transition: "border-color 0.2s" }}
        />
        {error && <div style={{ fontSize: 11, color: "#8B4A3A", letterSpacing: "0.1em" }}>Incorrect PIN</div>}
        <button onClick={checkPin} style={{ background: "#C9A84C", color: "#111", border: "none", padding: "10px 32px", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2, fontFamily: "Georgia, serif", width: "100%" }}>Unlock</button>
      </div>
    </div>
  );
}
export default function ReformRentalsCRM() {
  const [unlocked, setUnlocked] = useState(() => {
    const t = localStorage.getItem("crm_unlocked");
    if (!t) return false;
    return Date.now() - parseInt(t) < 8 * 60 * 60 * 1000;
  });

  const [tab, setTab] = useState("Dashboard");
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [partners, setPartners] = useState([]);
  const [payments, setPayments] = useState([]);
  const [focusCustomerId, setFocusCustomerId] = useState(null);
  const [focusMachineId, setFocusMachineId] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [alertsFromDb, setAlertsFromDb] = useState([]);

  useEffect(() => {
    supabase.from("alerts").select("*").eq("dismissed", false).then(({ data }) => {
      if (data) setAlertsFromDb(data);
    });
  }, []);

  useEffect(() => {
    if (!customers.length) return;
    const today = new Date();
    today.setHours(0,0,0,0);
    const generated = [];
    customers.forEach(cust => {
      if (cust.status === "Ended") return;
      if (cust.start_date) {
        const start = new Date(cust.start_date);
        if (!isNaN(start)) {
          const day42 = new Date(start); day42.setDate(day42.getDate() + 42);
          const day50 = new Date(start); day50.setDate(day50.getDate() + 50);
          if (today >= day42 && today <= day50) {
            generated.push({
              label: `6-week follow up · ${cust.name}`,
              detail: `Started ${start.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · Time to check in`,
              color: "#4CAF82",
              customer_id: cust.id,
              dismissed: false
            });
          }
        }
      }
      if (cust.renewal) {
        const d = new Date(cust.renewal);
        if (!isNaN(d)) {
          const daysUntil = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
          const noticeDays = cust.type === "Studio" ? 42 : 21;
          if (daysUntil <= noticeDays + 14 && daysUntil >= noticeDays + 4) {
            generated.push({
              label: `Renewal due · ${cust.name}`,
              detail: `${daysUntil} days until renewal`,
              color: "#C9A84C",
              customer_id: cust.id,
              type: "renewal",
              dismissed: false
            });
          }
        }
      }
    });
    if (generated.length === 0) return;
    const dismissed = JSON.parse(localStorage.getItem("dismissedAlerts") || "[]");
    const toInsert = generated.filter(a => !dismissed.includes(a.label));
    if (toInsert.length === 0) { supabase.from("alerts").select("*").then(({ data }) => { if (data) setAlertsFromDb(data); }); return; }
    supabase.from("alerts").upsert(toInsert, { onConflict: "label", ignoreDuplicates: true }).then(() => {
      supabase.from("alerts").select("*").then(({ data }) => {
        if (data) setAlertsFromDb(data);
      });
    });
  }, [customers]);
  const [rateHistories, setRateHistories] = useState([]);
  const [important, setImportant] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [lastAction, setLastAction] = useState(null);
  const [monthlyOut, setMonthlyOut] = useState(0);

  const undo = async () => {
    if (!lastAction) return;
    const { type, table, prev, id } = lastAction;
    if (type === "delete") {
      await supabase.from(table).insert([prev]);
      if (table === "customers") setCustomers(p => [...p, prev]);
      if (table === "machines") setMachines(p => [...p, prev]);
      if (table === "partners") setPartners(p => [...p, prev]);
    } else if (type === "edit") {
      await supabase.from(table).update(prev).eq("id", id);
      if (table === "customers") setCustomers(p => p.map(x => x.id === id ? prev : x));
      if (table === "machines") setMachines(p => p.map(x => x.id === id ? prev : x));
    } else if (type === "delete_note" || type === "insert_note" || type === "dismiss_alert") {
      if (lastAction.table) await supabase.from(lastAction.table).insert([lastAction.prev]);
      if (lastAction.restore) lastAction.restore(lastAction.prev);
    }
    if (type === "delete" && table === "enquiries") {
      if (window.__setEnquiries) window.__setEnquiries(p => [prev, ...p]);
    }
    setLastAction(null);
  };

  useEffect(() => { window.__setLastAction = setLastAction; window.__goToCustomer = goToCustomer; }, [setLastAction]);
  useEffect(() => { window.__rateHistories = rateHistories; }, [rateHistories]);

  useEffect(() => {
    supabase.from("rate_history").select("*").then(({ data }) => {
      if (data) setRateHistories(data);
    });
    supabase.from("notes").select("*").then(({ data }) => {
      if (!data) return;
      setImportant(data.filter(n => n.type === "important").map(n => ({ id: n.id, text: n.text })));
      setIdeas(data.filter(n => n.type === "idea").map(n => ({ id: n.id, text: n.text })));
    });
  }, []);


  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [c, m, p, pay] = await Promise.all([
        supabase.from("customers").select("*"),
        supabase.from("machines").select("*"),
        supabase.from("partners").select("*"),
        supabase.from("payments").select("*"),
      ]);
      if (c.data) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const updated = await Promise.all(c.data.map(async (customer) => {
          if (customer.status === "Ended") return customer;
          if (customer.status === "Pending") {
            const startDate = new Date(customer.start_date);
            if (!isNaN(startDate) && today >= startDate) {
              await supabase.from("customers").update({ status: "Active" }).eq("id", customer.id);
              return { ...customer, status: "Active" };
            }
            return customer;
          }
          if (!customer.renewal) return customer;
          if (customer.status === "Pending") {
            const startDate = new Date(customer.start_date);
            if (!isNaN(startDate) && today >= startDate) {
              await supabase.from("customers").update({ status: "Active" }).eq("id", customer.id);
              return { ...customer, status: "Active" };
            }
            return customer;
          }

          if (customer.status === "Ending") {
            const endDate = new Date(customer.renewal);
            if (today > endDate) {
              await supabase.from("customers").update({ status: "Ended" }).eq("id", customer.id);
              return { ...customer, status: "Ended" };
            }
            return customer;
          }
          let d = new Date(customer.renewal);
          if (isNaN(d)) return customer;
          const noticeDays = customer.type === "Studio" ? 42 : 21;
          const cutoff = new Date(d);
          cutoff.setDate(cutoff.getDate() - noticeDays);
          const isRolling = customer.term === "Rolling" || customer.extension === "Rolling";
          if (!isRolling) return customer;
          if (customer.status === "Ending") return customer;
          if (today >= cutoff) {
            while (today >= new Date(new Date(d).setDate(d.getDate() - noticeDays))) {
              d.setMonth(d.getMonth() + 1);
            }
            const newRenewal = d.toISOString().split("T")[0];
            if (newRenewal !== customer.renewal) {
              const updates = { renewal: newRenewal };
              await supabase.from("customers").update(updates).eq("id", customer.id);
              return { ...customer, ...updates };
            }
          }
          return customer;
        }));

        // Auto-apply any scheduled rate change whose start date has arrived
        const { data: rhData } = await supabase.from("rate_history").select("*");
        if (rhData && rhData.length) {
          const todayForRates = new Date(); todayForRates.setHours(0, 0, 0, 0);
          for (let i = 0; i < updated.length; i++) {
            const cust = updated[i];
            const custRows = rhData.filter(h => h.customer_id === cust.id).sort((a, b) => new Date(b.from_date) - new Date(a.from_date));
            const applicable = custRows.find(h => new Date(h.from_date) <= todayForRates);
            if (applicable && !isNaN(applicable.rate)) {
              const currentNum = parseFloat((cust.monthly || "0").replace(/[£,]/g, ""));
              if (Math.round(applicable.rate) !== Math.round(currentNum)) {
                const newMonthly = "£" + applicable.rate;
                await supabase.from("customers").update({ monthly: newMonthly }).eq("id", cust.id);
                updated[i] = { ...cust, monthly: newMonthly };
              }
            }
          }
        }

        setCustomers(updated);
      }
      if (m.data) setMachines(m.data);
      if (p.data) setPartners(p.data);
      if (pay.data) setPayments(pay.data);
      setLoading(false);
      // Load finance totals for dashboard
      const [expR, mfR, dlR] = await Promise.all([
        supabase.from("expenses").select("amount,recurring"),
        supabase.from("machine_finance").select("monthly_payment, status"),
        supabase.from("dad_loan").select("planned_monthly").order("created_at").limit(1),
      ]);
      const expTotal = (expR.data || []).filter(e => e.recurring).reduce((s, e) => s + parseFloat(e.amount), 0);
      const mfTotal = (mfR.data || []).filter(f => f.status !== "Pending" && f.status !== "Finished").reduce((s, f) => s + parseFloat(f.monthly_payment), 0);
      const dadPlanned = (dlR.data && dlR.data[0]) ? parseFloat(dlR.data[0].planned_monthly || 0) : 0;
      setMonthlyOut(expTotal + mfTotal + dadPlanned);
    };
    load();
  }, []);

  const goToCustomer = (id) => { setFocusCustomerId(id); setTab("Customers"); };
  const goToMachine = (id) => { setFocusMachineId(id); setTab("Machines"); };

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  if (loading) return (
    <div style={{ ...styles.app, alignItems: "center", justifyContent: "center" }}>
      <div style={{ color: MUTED, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase" }}>Loading...</div>
    </div>
  );

  return (
    <div style={styles.app} className="rr-app">
      <header style={styles.header} className="rr-header">
        <div style={styles.logo}>ReformRentals</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {lastAction && (
            <button onClick={undo} style={{ ...styles.btnGhost, color: "#4CAF82", borderColor: "#4CAF8244", fontSize: 11 }}>↩ Undo</button>
          )}
          <span style={styles.badge}>CRM V2</span>
        </div>
      </header>
      {!isMobile && <nav style={styles.nav}>
        {TABS.map(t => <button key={t} style={styles.navTab(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
      </nav>}
      <main style={styles.body} className="rr-body">
        {tab === "Dashboard" && <Dashboard customers={customers} machines={machines} onSelectCustomer={goToCustomer} dismissedAlerts={dismissedAlerts} setDismissedAlerts={setDismissedAlerts} alertsFromDb={alertsFromDb} setAlertsFromDb={setAlertsFromDb} important={important} onDeleteImportant={async (id) => { await supabase.from("notes").delete().eq("id", id); setImportant(p => p.filter(i => i.id !== id)); }} onSelectTab={setTab} monthlyOut={monthlyOut} />}
        {tab === "Customers" && <Customers customers={customers} setCustomers={setCustomers} machines={machines} setMachines={setMachines} focusId={focusCustomerId} clearFocus={() => setFocusCustomerId(null)} onSelectMachine={goToMachine} setLastAction={setLastAction} rateHistories={rateHistories} setRateHistories={setRateHistories} />}
        {tab === "Machines" && <Machines customers={customers} machines={machines} setMachines={setMachines} focusMachineId={focusMachineId} clearMachineFocus={() => setFocusMachineId(null)} setLastAction={setLastAction} />}
        {tab === "Partners" && <Partners partners={partners} setPartners={setPartners} />}
        {tab === "Payments" && <Payments payments={payments} setPayments={setPayments} customers={customers} />}
        {tab === "Systems" && <Systems customers={customers} />}


        {tab === "Enquiries" && <Enquiries />}
        {tab === "Finance" && <Finance />}
        {tab === "Growth" && <Growth important={important} setImportant={setImportant} ideas={ideas} setIdeas={setIdeas} />}
        {tab === "Investment" && <InvestmentDashboard customers={customers} machines={machines} monthlyOut={monthlyOut} />}
      </main>
      {isMobile && (
        <>
          {moreOpen && (
            <div onClick={() => setMoreOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 90 }}>
              <div onClick={e => e.stopPropagation()} style={{ position: "absolute", left: 0, right: 0, bottom: 0, background: CARD, borderTop: `1px solid ${BORDER}`, borderRadius: "16px 16px 0 0", padding: "16px 16px calc(24px + env(safe-area-inset-bottom))" }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: BORDER, margin: "0 auto 16px" }} />
                {MOBILE_MORE.map(t => (
                  <button key={t} onClick={() => { setTab(t); setMoreOpen(false); }} style={{ display: "block", width: "100%", textAlign: "left", background: tab === t ? "rgba(201,168,76,0.12)" : "transparent", color: tab === t ? GOLD : TEXT, border: "none", borderBottom: `1px solid ${BORDER}`, padding: "16px 8px", fontSize: 16, fontFamily: "'Cormorant Garant', serif", letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer" }}>{t}</button>
                ))}
              </div>
            </div>
          )}
          <nav className="rr-bottomnav" style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 100, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", background: "#111", borderTop: `1px solid ${BORDER}`, paddingBottom: "env(safe-area-inset-bottom)" }}>
            {[...MOBILE_MAIN, "More"].map(t => {
              const active = t === "More" ? MOBILE_MORE.includes(tab) : tab === t;
              return (
                <button key={t} onClick={() => t === "More" ? setMoreOpen(o => !o) : (setTab(t), setMoreOpen(false))} style={{ background: "none", border: "none", height: 60, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, color: active ? GOLD : MUTED, cursor: "pointer", borderTop: `2px solid ${active ? GOLD : "transparent"}` }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{TAB_ICONS[t]}</span>
                  <span style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>{t}</span>
                </button>
              );
            })}
          </nav>
        </>
      )}
    </div>
  );
}

function Finance() {
  const [expenses, setExpenses] = useState([]);
  const [machineFinance, setMachineFinance] = useState([]);
  const [financePayments, setFinancePayments] = useState([]);
  const [dadLoan, setDadLoan] = useState(null);
  const [dadPayments, setDadPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showFinanceForm, setShowFinanceForm] = useState(false);
  const [showDadForm, setShowDadForm] = useState(false);
  const [showDadPaymentForm, setShowDadPaymentForm] = useState(false);
  const [showFinancePaymentForm, setShowFinancePaymentForm] = useState(null);
  const [editingFinanceId, setEditingFinanceId] = useState(null);
  const [financeEditForm, setFinanceEditForm] = useState({});
  const [confirmDeleteFinance, setConfirmDeleteFinance] = useState(null);
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(null);
  const blankExpense = { name: "", amount: "", date: new Date().toISOString().split("T")[0], category: "General", recurring: false, recurring_day: "" };
  const blankFinance = { machine_id: "", lender: "Align", total_amount: "", monthly_payment: "", start_date: "", payment_day: "", notes: "", status: "Active" };
  const blankDadPayment = { amount: "", date: new Date().toISOString().split("T")[0], note: "" };
  const [expenseForm, setExpenseForm] = useState(blankExpense);
  const [financeForm, setFinanceForm] = useState(blankFinance);
  const [dadForm, setDadForm] = useState({ total_borrowed: "", planned_monthly: "" });
  const [editingPlanned, setEditingPlanned] = useState(false);
  const [plannedInput, setPlannedInput] = useState("");
  const [dadPaymentForm, setDadPaymentForm] = useState(blankDadPayment);
  const [financePaymentForm, setFinancePaymentForm] = useState({ amount: "", date: new Date().toISOString().split("T")[0] });

  useEffect(() => {
    const load = async () => {
      const [e, mf, fp, dl, dp] = await Promise.all([
        supabase.from("expenses").select("*").order("date", { ascending: false }),
        supabase.from("machine_finance").select("*").order("created_at"),
        supabase.from("machine_finance_payments").select("*").order("date", { ascending: false }),
        supabase.from("dad_loan").select("*").order("created_at").limit(1),
        supabase.from("dad_loan_payments").select("*").order("date", { ascending: false }),
      ]);
      if (e.data) setExpenses(e.data);
      if (mf.data) setMachineFinance(mf.data);
      if (fp.data) setFinancePayments(fp.data);
      if (dl.data && dl.data.length > 0) setDadLoan(dl.data[0]);
      if (dp.data) setDadPayments(dp.data);
      setLoading(false);
      if (mf.data && fp.data) {
        const parseLocalDate = (dateStr) => {
          if (!dateStr) return null;
          const [y, m, dd] = dateStr.split("-").map(Number);
          return new Date(y, m - 1, dd);
        };
        const todayLocal = new Date();
        todayLocal.setHours(0, 0, 0, 0);
        const newPayments = [];
        for (const f of mf.data) {
          if (f.status === "Pending" || f.status === "Finished") continue;
          const start = parseLocalDate(f.start_date);
          if (!start) continue;
          let d = new Date(start.getFullYear(), start.getMonth(), f.payment_day);
          if (d < start) d.setMonth(d.getMonth() + 1);
          while (d <= todayLocal) {
            const alreadyLogged = fp.data.some(p => {
              const pd = parseLocalDate(p.date);
              return p.finance_id === f.id && pd && pd.getMonth() === d.getMonth() && pd.getFullYear() === d.getFullYear();
            });
            if (!alreadyLogged) {
              const dateStr = d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
              const { data } = await supabase.from("machine_finance_payments").insert({ finance_id: f.id, amount: f.monthly_payment, date: dateStr }).select().single();
              if (data) newPayments.push(data);
            }
            d.setMonth(d.getMonth() + 1);
          }
        }
        if (newPayments.length > 0) setFinancePayments(prev => [...newPayments, ...prev]);
      }
    };
    load();
  }, []);

  const saveExpense = async () => {
    if (!expenseForm.name || !expenseForm.amount || !expenseForm.date) return;
    const row = { ...expenseForm, amount: parseFloat(expenseForm.amount), recurring_day: expenseForm.recurring ? parseInt(expenseForm.recurring_day) || new Date(expenseForm.date).getDate() : null };
    const { data } = await supabase.from("expenses").insert(row).select().single();
    if (data) setExpenses(prev => [data, ...prev]);
    setExpenseForm(blankExpense); setShowExpenseForm(false);
  };
  const deleteExpense = async (id) => { await supabase.from("expenses").delete().eq("id", id); setExpenses(prev => prev.filter(e => e.id !== id)); };
  const saveFinance = async () => {
    if (!financeForm.machine_id || !financeForm.total_amount || !financeForm.monthly_payment || !financeForm.start_date || !financeForm.payment_day) return;
    const row = { ...financeForm, total_amount: parseFloat(financeForm.total_amount), monthly_payment: parseFloat(financeForm.monthly_payment), payment_day: parseInt(financeForm.payment_day) };
    const { data } = await supabase.from("machine_finance").insert(row).select().single();
    if (data) setMachineFinance(prev => [...prev, data]);
    setFinanceForm(blankFinance); setShowFinanceForm(false);
  };
  const saveDadLoan = async () => {
    if (!dadForm.total_borrowed) return;
    const { data } = await supabase.from("dad_loan").insert({ total_borrowed: parseFloat(dadForm.total_borrowed), planned_monthly: parseFloat(dadForm.planned_monthly || 0) }).select().single();
    if (data) setDadLoan(data); setShowDadForm(false);
  };
  const savePlannedMonthly = async () => {
    const val = parseFloat(plannedInput || 0);
    await supabase.from("dad_loan").update({ planned_monthly: val }).eq("id", dadLoan.id);
    setDadLoan(prev => ({ ...prev, planned_monthly: val }));
    setEditingPlanned(false);
  };
  const saveDadPayment = async () => {
    if (!dadPaymentForm.amount || !dadPaymentForm.date) return;
    const { data } = await supabase.from("dad_loan_payments").insert({ ...dadPaymentForm, amount: parseFloat(dadPaymentForm.amount) }).select().single();
    if (data) setDadPayments(prev => [data, ...prev]);
    setDadPaymentForm(blankDadPayment); setShowDadPaymentForm(false);
  };
  const saveFinancePayment = async (financeId) => {
    if (!financePaymentForm.amount || !financePaymentForm.date) return;
    const { data } = await supabase.from("machine_finance_payments").insert({ finance_id: financeId, amount: parseFloat(financePaymentForm.amount), date: financePaymentForm.date }).select().single();
    if (data) setFinancePayments(prev => [data, ...prev]);
    setFinancePaymentForm({ amount: "", date: new Date().toISOString().split("T")[0] }); setShowFinancePaymentForm(null);
  };
  const startEditFinance = (f) => { setEditingFinanceId(f.id); setFinanceEditForm({ ...f }); };
  const saveFinanceEdit = async () => {
    const { id, ...rest } = financeEditForm;
    const row = { ...rest, total_amount: parseFloat(rest.total_amount), monthly_payment: parseFloat(rest.monthly_payment), payment_day: parseInt(rest.payment_day) };
    const { error } = await supabase.from("machine_finance").update(row).eq("id", id);
    if (!error) setMachineFinance(prev => prev.map(x => x.id === id ? { ...x, ...row } : x));
    setEditingFinanceId(null);
  };
  const deleteFinance = async (id) => {
    await supabase.from("machine_finance").delete().eq("id", id);
    setMachineFinance(prev => prev.filter(x => x.id !== id));
    setFinancePayments(prev => prev.filter(p => p.finance_id !== id));
    setConfirmDeleteFinance(null);
  };
  const deleteFinancePayment = async (id) => {
    await supabase.from("machine_finance_payments").delete().eq("id", id);
    setFinancePayments(prev => prev.filter(x => x.id !== id));
    setConfirmDeletePayment(null);
  };

  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const monthlyExpenses = expenses.filter(e => e.recurring || (new Date(e.date).getMonth() === currentMonth && new Date(e.date).getFullYear() === currentYear)).reduce((s, e) => s + parseFloat(e.amount), 0);
  const monthlyFinance = machineFinance.filter(f => f.status !== "Pending" && f.status !== "Finished").reduce((s, f) => s + parseFloat(f.monthly_payment), 0);
  const totalMonthlyOut = monthlyExpenses + monthlyFinance + (dadLoan ? parseFloat(dadLoan.planned_monthly || 0) : 0);
  const dadTotalBorrowed = dadLoan ? parseFloat(dadLoan.total_borrowed) : 0;
  const dadTotalPaid = dadPayments.reduce((s, p) => s + parseFloat(p.amount), 0);
  const dadOutstanding = Math.max(0, dadTotalBorrowed - dadTotalPaid);
  const ordinal = (n) => { const s = ["th","st","nd","rd"]; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); };
  const sections = ["overview", "expenses", "machine finance", "dads loan"];

  if (loading) return <div style={{ color: MUTED, fontSize: 12, letterSpacing: "0.2em" }}>Loading...</div>;

  return (
    <div>
      <p style={styles.sectionTitle}>Finance</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {sections.map(s => <button key={s} onClick={() => setActiveSection(s)} style={activeSection === s ? styles.btn : styles.btnGhost}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>)}
      </div>

      {activeSection === "overview" && (
        <div>
          <div style={styles.grid3}>
            {[{ label: "Total Monthly Out", value: "£" + totalMonthlyOut.toLocaleString(), sub: "Expenses + finance" }, { label: "Monthly Finance", value: "£" + monthlyFinance.toLocaleString(), sub: machineFinance.length + " agreement" + (machineFinance.length !== 1 ? "s" : "") }, { label: "Dad Loan Outstanding", value: "£" + dadOutstanding.toLocaleString(), sub: "£" + dadTotalPaid.toLocaleString() + " paid back" }].map(s => (
              <div key={s.label} style={styles.statCard}><div style={styles.statLabel}>{s.label}</div><div style={styles.statValue}>{s.value}</div><div style={styles.statSub}>{s.sub}</div></div>
            ))}
          </div>
          {machineFinance.filter(f => {
            const paidCheck = financePayments.filter(p => p.finance_id === f.id).reduce((s, p) => s + parseFloat(p.amount), 0);
            const pctCheck = Math.min(100, Math.round((paidCheck / f.total_amount) * 100));
            return pctCheck < 100 && f.status !== "Finished";
          }).length > 0 && (
            <div style={styles.card}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>Machine Finance Progress</span></div>
              <div style={{ padding: "16px 24px" }}>
                {machineFinance.filter(f => {
                  const paidCheck = financePayments.filter(p => p.finance_id === f.id).reduce((s, p) => s + parseFloat(p.amount), 0);
                  const pctCheck = Math.min(100, Math.round((paidCheck / f.total_amount) * 100));
                  return pctCheck < 100 && f.status !== "Finished";
                }).map(f => {
                  const paid = financePayments.filter(p => p.finance_id === f.id).reduce((s, p) => s + parseFloat(p.amount), 0);
                  const pct = Math.min(100, Math.round((paid / f.total_amount) * 100));
                  const payoffDate = computeActualPayoffDate(f, paid);
                  return (
                    <div key={f.id} style={{ marginBottom: 20 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ fontSize: 13, color: TEXT }}>{f.machine_id} · {f.lender}<span style={{ fontSize: 11, color: MUTED, marginLeft: 8 }}>· out on {ordinal(f.payment_day)} · £{parseFloat(f.monthly_payment).toLocaleString()}/mo</span></span><span style={{ fontSize: 12, color: MUTED }}>£{Math.round(Math.max(0, f.total_amount - paid)).toLocaleString()} remaining</span></div>
                      <div style={{ background: "#2a2a2a", borderRadius: 2, height: 6 }}><div style={{ background: pct >= 100 ? "#4CAF82" : GOLD, height: 6, borderRadius: 2, width: pct + "%", transition: "width 0.4s ease" }} /></div>
                      <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{pct}% paid · £{Math.round(paid).toLocaleString()} of £{parseFloat(f.total_amount).toLocaleString()}{pct >= 100 ? " · Paid off" : payoffDate ? " · Projected payoff " + payoffDate.toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {dadLoan && (
            <div style={styles.card}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>Dad Loan</span></div>
              <div style={{ padding: "16px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ fontSize: 13, color: TEXT }}>£{dadTotalPaid.toLocaleString()} paid back</span><span style={{ fontSize: 12, color: MUTED }}>£{dadOutstanding.toLocaleString()} remaining</span></div>
                <div style={{ background: "#2a2a2a", borderRadius: 2, height: 6 }}><div style={{ background: dadOutstanding === 0 ? "#4CAF82" : GOLD, height: 6, borderRadius: 2, width: Math.min(100, Math.round((dadTotalPaid / dadTotalBorrowed) * 100)) + "%", transition: "width 0.4s ease" }} /></div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>{Math.min(100, Math.round((dadTotalPaid / dadTotalBorrowed) * 100))}% paid · Total borrowed £{dadTotalBorrowed.toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === "expenses" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Expenses · {expenses.length} total</p>
            <button style={styles.btn} onClick={() => setShowExpenseForm(true)}>+ Add Expense</button>
          </div>
          {showExpenseForm && (
            <div style={{ ...styles.card, marginBottom: 20 }}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>New Expense</span><button style={styles.btnGhost} onClick={() => setShowExpenseForm(false)}>✕ Cancel</button></div>
              <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div style={fieldWrap}><label style={labelStyle}>Name</label><input value={expenseForm.name} onChange={e => setExpenseForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. Insurance" /></div>
                <div style={fieldWrap}><label style={labelStyle}>Amount (£)</label><input type="number" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} /></div>
                <div style={fieldWrap}><label style={labelStyle}>Date</label><input type="date" value={expenseForm.date} onChange={e => setExpenseForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} /></div>
                <div style={fieldWrap}><label style={labelStyle}>Category</label><select value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))} style={selectStyle}>{["General","Insurance","Software","Fuel","Marketing","Equipment","Other"].map(o => <option key={o}>{o}</option>)}</select></div>
                <div style={fieldWrap}><label style={labelStyle}>Recurring Monthly?</label><select value={expenseForm.recurring ? "yes" : "no"} onChange={e => setExpenseForm(f => ({ ...f, recurring: e.target.value === "yes" }))} style={selectStyle}><option value="no">No</option><option value="yes">Yes</option></select></div>
                {expenseForm.recurring && <div style={fieldWrap}><label style={labelStyle}>Day of month</label><input type="number" min="1" max="31" value={expenseForm.recurring_day} onChange={e => setExpenseForm(f => ({ ...f, recurring_day: e.target.value }))} style={inputStyle} placeholder="e.g. 1" /></div>}
                <div style={{ gridColumn: "1 / -1" }}><button style={{ ...styles.btn, padding: "11px 32px" }} onClick={saveExpense}>Save Expense</button></div>
              </div>
            </div>
          )}
          <div style={styles.card}>
            <table style={styles.table}>
              <thead><tr>{["Name","Category","Amount","Date","Recurring",""].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e.id}>
                    <td style={styles.td}>{e.name}</td>
                    <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{e.category}</td>
                    <td style={{ ...styles.td, color: GOLD }}>£{parseFloat(e.amount).toLocaleString()}</td>
                    <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{e.recurring ? "Every " + ordinal(e.recurring_day || new Date(e.date).getDate()) : new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                    <td style={styles.td}>{e.recurring ? <Pill label="Active" /> : <span style={{ color: MUTED, fontSize: 12 }}>One-off</span>}</td>
                    <td style={styles.td}><button onClick={() => deleteExpense(e.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button></td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={6} style={{ ...styles.td, color: MUTED, textAlign: "center" }}>No expenses yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeSection === "machine finance" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Machine Finance · {machineFinance.length} agreements</p>
            <button style={styles.btn} onClick={() => setShowFinanceForm(true)}>+ Add Agreement</button>
          </div>
          {showFinanceForm && (
            <div style={{ ...styles.card, marginBottom: 20 }}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>New Finance Agreement</span><button style={styles.btnGhost} onClick={() => setShowFinanceForm(false)}>✕ Cancel</button></div>
              <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                {[["Machine ID","machine_id","text"],["Lender","lender","text"],["Total Amount (£)","total_amount","number"],["Monthly Payment (£)","monthly_payment","number"],["Start Date","start_date","date"],["Payment Day (1-31)","payment_day","number"]].map(([label, key, type]) => (
                  <div key={key} style={fieldWrap}><label style={labelStyle}>{label}</label><input type={type} value={financeForm[key]} onChange={e => setFinanceForm(f => ({ ...f, [key]: e.target.value }))} style={inputStyle} /></div>
                ))}
                <div style={fieldWrap}><label style={labelStyle}>Notes</label><input value={financeForm.notes} onChange={e => setFinanceForm(f => ({ ...f, notes: e.target.value }))} style={inputStyle} /></div>
                <div style={{ gridColumn: "1 / -1" }}><button style={{ ...styles.btn, padding: "11px 32px" }} onClick={saveFinance}>Save Agreement</button></div>
              </div>
            </div>
          )}
          {machineFinance.map(f => {
            const payments = financePayments.filter(p => p.finance_id === f.id);
            const paid = payments.reduce((s, p) => s + parseFloat(p.amount), 0);
            const pct = Math.min(100, Math.round((paid / f.total_amount) * 100));
            const payoffDate = computeActualPayoffDate(f, paid);
            return (
              <div key={f.id} style={{ ...styles.card, marginBottom: 16 }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardTitle}>{f.machine_id} · {f.lender}</span>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: GOLD }}>£{parseFloat(f.monthly_payment).toLocaleString()}/month</span>
                    <button onClick={async () => { const newStatus = f.status === "Pending" ? "Active" : "Pending"; await supabase.from("machine_finance").update({ status: newStatus }).eq("id", f.id); setMachineFinance(prev => prev.map(x => x.id === f.id ? { ...x, status: newStatus } : x)); }} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px", color: f.status === "Pending" ? "#6B8FA8" : f.status === "Finished" ? "#4CAF82" : GOLD, borderColor: f.status === "Pending" ? "#6B8FA866" : f.status === "Finished" ? "#4CAF8266" : undefined }}>{f.status === "Pending" ? "Pending" : f.status === "Finished" ? "Finished" : "Active"}</button>
                    <button onClick={async () => { const newStatus = f.status === "Finished" ? "Active" : "Finished"; await supabase.from("machine_finance").update({ status: newStatus }).eq("id", f.id); setMachineFinance(prev => prev.map(x => x.id === f.id ? { ...x, status: newStatus } : x)); }} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px", color: f.status === "Finished" ? "#4CAF82" : MUTED, borderColor: f.status === "Finished" ? "#4CAF8266" : undefined }}>{f.status === "Finished" ? "✓ Finished" : "Mark Finished"}</button>
                    <button onClick={() => startEditFinance(f)} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px" }}>Edit</button>
                    {confirmDeleteFinance === f.id ? (
                      <>
                        <button onClick={() => deleteFinance(f.id)} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px", color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Confirm</button>
                        <button onClick={() => setConfirmDeleteFinance(null)} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px" }}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => setConfirmDeleteFinance(f.id)} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px", color: MAHOGANY, borderColor: MAHOGANY + "66" }}>Delete</button>
                    )}
                  </div>
                </div>
                {editingFinanceId === f.id ? (
                  <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    {[["Machine ID","machine_id","text"],["Lender","lender","text"],["Total Amount (£)","total_amount","number"],["Monthly Payment (£)","monthly_payment","number"],["Start Date","start_date","date"],["Payment Day (1-31)","payment_day","number"]].map(([label, key, type]) => (
                      <div key={key} style={fieldWrap}><label style={labelStyle}>{label}</label><input type={type} value={financeEditForm[key] ?? ""} onChange={e => setFinanceEditForm(fo => ({ ...fo, [key]: e.target.value }))} style={inputStyle} /></div>
                    ))}
                    <div style={fieldWrap}><label style={labelStyle}>Notes</label><input value={financeEditForm.notes ?? ""} onChange={e => setFinanceEditForm(fo => ({ ...fo, notes: e.target.value }))} style={inputStyle} /></div>
                    <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
                      <button style={{ ...styles.btn, padding: "11px 32px" }} onClick={saveFinanceEdit}>Save Changes</button>
                      <button style={styles.btnGhost} onClick={() => setEditingFinanceId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                <div style={{ padding: "16px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ fontSize: 13, color: TEXT }}>£{Math.round(paid).toLocaleString()} paid</span><span style={{ fontSize: 12, color: MUTED }}>£{Math.round(Math.max(0, f.total_amount - paid)).toLocaleString()} remaining</span></div>
                  <div style={{ background: "#2a2a2a", borderRadius: 2, height: 6, marginBottom: 6 }}><div style={{ background: pct >= 100 ? "#4CAF82" : GOLD, height: 6, borderRadius: 2, width: pct + "%", transition: "width 0.4s ease" }} /></div>
                  <div style={{ fontSize: 11, color: MUTED, marginBottom: 16 }}>{pct}% paid · Total £{parseFloat(f.total_amount).toLocaleString()} · Payment on {ordinal(f.payment_day)} of each month{pct >= 100 ? " · Paid off" : payoffDate ? " · Projected payoff " + payoffDate.toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : ""}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>Payment History · {payments.length}</span>
                    <button onClick={() => setShowFinancePaymentForm(f.id)} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 12px" }}>+ Log Payment</button>
                  </div>
                  {showFinancePaymentForm === f.id && (
                    <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                      <input type="number" placeholder="Amount £" value={financePaymentForm.amount} onChange={e => setFinancePaymentForm(p => ({ ...p, amount: e.target.value }))} style={{ ...inputStyle, width: 120 }} />
                      <input type="date" value={financePaymentForm.date} onChange={e => setFinancePaymentForm(p => ({ ...p, date: e.target.value }))} style={{ ...inputStyle, width: 160 }} />
                      <button onClick={() => saveFinancePayment(f.id)} style={styles.btn}>Save</button>
                      <button onClick={() => setShowFinancePaymentForm(null)} style={styles.btnGhost}>Cancel</button>
                    </div>
                  )}
                  <table style={styles.table}>
                    <thead><tr>{["Date","Amount",""].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {payments.slice(0, 6).map(p => (
                        <tr key={p.id}>
                          <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                          <td style={{ ...styles.td, color: GOLD }}>£{parseFloat(p.amount).toLocaleString()}</td>
                          <td style={{ ...styles.td, textAlign: "right" }}>
                            {confirmDeletePayment === p.id ? (
                              <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                <button onClick={() => deleteFinancePayment(p.id)} style={{ background: "none", border: `1px solid ${MAHOGANY}66`, color: MAHOGANY, cursor: "pointer", fontSize: 10, padding: "3px 8px", borderRadius: 2, fontFamily: "'Cormorant Garant', serif" }}>Sure?</button>
                                <button onClick={() => setConfirmDeletePayment(null)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 11 }}>✕</button>
                              </span>
                            ) : (
                              <button onClick={() => setConfirmDeletePayment(p.id)} style={{ background: "none", border: "none", color: MUTED, cursor: "pointer", fontSize: 13 }}>✕</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {payments.length === 0 && <tr><td colSpan={3} style={{ ...styles.td, color: MUTED }}>No payments yet</td></tr>}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            );
          })}
          {machineFinance.length === 0 && <div style={{ ...styles.card, padding: 24, color: MUTED, fontSize: 12 }}>No finance agreements yet</div>}
        </div>
      )}

      {activeSection === "dads loan" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <p style={{ ...styles.sectionTitle, marginBottom: 0 }}>Dad Loan</p>
            {!dadLoan && <button style={styles.btn} onClick={() => setShowDadForm(true)}>+ Set Up Loan</button>}
            {dadLoan && <button style={styles.btn} onClick={() => setShowDadPaymentForm(true)}>+ Log Repayment</button>}
          </div>
          {showDadForm && (
            <div style={{ ...styles.card, marginBottom: 20 }}>
              <div style={styles.cardHeader}><span style={styles.cardTitle}>Set Up Loan</span><button style={styles.btnGhost} onClick={() => setShowDadForm(false)}>✕ Cancel</button></div>
              <div style={{ padding: 24, display: "flex", gap: 16 }}>
                <div style={fieldWrap}><label style={labelStyle}>Total Borrowed (£)</label><input type="number" value={dadForm.total_borrowed} onChange={e => setDadForm(f => ({ ...f, total_borrowed: e.target.value }))} style={inputStyle} /></div>
                <div style={fieldWrap}><label style={labelStyle}>Planned Monthly Repayment (£)</label><input type="number" value={dadForm.planned_monthly} onChange={e => setDadForm(f => ({ ...f, planned_monthly: e.target.value }))} style={inputStyle} placeholder="0" /></div>
                <div style={{ display: "flex", alignItems: "flex-end" }}><button style={styles.btn} onClick={saveDadLoan}>Save</button></div>
              </div>
            </div>
          )}
          {dadLoan && (
            <div>
              <div style={{ ...styles.statCard, marginBottom: 16 }}>
                <div style={styles.statLabel}>Outstanding</div>
                <div style={styles.statValue}>£{dadOutstanding.toLocaleString()}</div>
                <div style={styles.statSub}>£{dadTotalPaid.toLocaleString()} paid of £{dadTotalBorrowed.toLocaleString()}</div>
                <div style={{ background: "#2a2a2a", borderRadius: 2, height: 6, marginTop: 12 }}><div style={{ background: dadOutstanding === 0 ? "#4CAF82" : GOLD, height: 6, borderRadius: 2, width: Math.min(100, Math.round((dadTotalPaid / dadTotalBorrowed) * 100)) + "%", transition: "width 0.4s ease" }} /></div>
              </div>
              <div style={{ ...styles.statCard, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={styles.statLabel}>Planned Monthly Repayment</div>
                  {editingPlanned ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                      <span style={{ color: MUTED }}>£</span>
                      <input type="number" value={plannedInput} onChange={e => setPlannedInput(e.target.value)} style={{ ...inputStyle, width: 120 }} />
                      <button onClick={savePlannedMonthly} style={styles.btn}>Save</button>
                      <button onClick={() => setEditingPlanned(false)} style={styles.btnGhost}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ ...styles.statValue, fontSize: 22 }}>£{parseFloat(dadLoan.planned_monthly || 0).toLocaleString()}</div>
                  )}
                  <div style={styles.statSub}>Counted in Total Monthly Out · doesn't affect the payoff above</div>
                </div>
                {!editingPlanned && <button onClick={() => { setPlannedInput(String(dadLoan.planned_monthly || 0)); setEditingPlanned(true); }} style={styles.btnGhost}>Edit</button>}
              </div>
              {showDadPaymentForm && (
                <div style={{ ...styles.card, marginBottom: 16 }}>
                  <div style={styles.cardHeader}><span style={styles.cardTitle}>Log Repayment</span><button style={styles.btnGhost} onClick={() => setShowDadPaymentForm(false)}>✕ Cancel</button></div>
                  <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                    <div style={fieldWrap}><label style={labelStyle}>Amount (£)</label><input type="number" value={dadPaymentForm.amount} onChange={e => setDadPaymentForm(f => ({ ...f, amount: e.target.value }))} style={inputStyle} /></div>
                    <div style={fieldWrap}><label style={labelStyle}>Date</label><input type="date" value={dadPaymentForm.date} onChange={e => setDadPaymentForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} /></div>
                    <div style={fieldWrap}><label style={labelStyle}>Note (optional)</label><input value={dadPaymentForm.note} onChange={e => setDadPaymentForm(f => ({ ...f, note: e.target.value }))} style={inputStyle} /></div>
                    <div style={{ gridColumn: "1 / -1" }}><button style={styles.btn} onClick={saveDadPayment}>Save Repayment</button></div>
                  </div>
                </div>
              )}
              <div style={styles.card}>
                <div style={styles.cardHeader}><span style={styles.cardTitle}>Repayment History · {dadPayments.length}</span></div>
                <table style={styles.table}>
                  <thead><tr>{["Date","Amount","Note"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {dadPayments.map(p => (<tr key={p.id}><td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{new Date(p.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td><td style={{ ...styles.td, color: GOLD }}>£{parseFloat(p.amount).toLocaleString()}</td><td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{p.note || "—"}</td></tr>))}
                    {dadPayments.length === 0 && <tr><td colSpan={3} style={{ ...styles.td, color: MUTED }}>No repayments logged yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── INVESTMENT DASHBOARD ─────────────────────────────────
function quarterOf(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const q = Math.floor(d.getMonth() / 3) + 1;
  return { key: d.getFullYear() + "-Q" + q, label: "Q" + q + " " + d.getFullYear(), sortKey: d.getFullYear() * 10 + q };
}

function machineCount(machineField) {
  if (!machineField) return 1;
  const arr = machineField.split(",").map(m => m.trim()).filter(Boolean);
  return arr.length || 1;
}

function calcFleetMetrics(customers, machines, extraTotal, extraEarning) {
  extraTotal = extraTotal || 0; extraEarning = extraEarning || 0;
  const reformerMachines = machines.filter(m => m.model !== "RR Chair");
  const totalReformers = reformerMachines.length + extraTotal;
  const earning = reformerMachines.filter(m => m.status === "Deployed").length + extraEarning;
  const utilisation = totalReformers > 0 ? (earning / totalReformers) * 100 : 0;
  return { totalReformers, earning, utilisation };
}

function calcFinanceMetrics(customers, monthlyOut, emergencyPot, extraIncome, extraRepayment, extraEarningReformers) {
  extraIncome = extraIncome || 0; extraRepayment = extraRepayment || 0; extraEarningReformers = extraEarningReformers || 0;
  const monthlyIncome = customers.filter(c => c.status === "Active" || c.status === "Ending").reduce((s, c) => {
    const v = parseFloat((c.monthly || "0").replace(/[£,]/g, ""));
    return s + (isNaN(v) ? 0 : v);
  }, 0) + extraIncome;
  const monthlyRepayments = (monthlyOut || 0) + extraRepayment;
  const coverage = monthlyIncome > 0 ? (monthlyRepayments / monthlyIncome) * 100 : 0;
  const earningCount = customers.filter(c => c.status === "Active" || c.status === "Ending").reduce((s, c) => s + machineCount(c.machine), 0) + extraEarningReformers;
  const avgRatePerReformer = earningCount > 0 ? monthlyIncome / earningCount : 0;
  const minReformersRequired = avgRatePerReformer > 0 ? Math.ceil(monthlyRepayments / avgRatePerReformer) : null;
  const emergencyBuffer = monthlyRepayments > 0 ? emergencyPot / monthlyRepayments : null;
  return { monthlyIncome, monthlyRepayments, coverage, minReformersRequired, emergencyBuffer, avgRatePerReformer };
}

function calcRetentionMetrics(customers) {
  const today = new Date();
  // Due = original end date has passed, OR they've already committed to a renewal (extension added)
  const dueCustomers = customers.filter(c =>
    (c.original_end_date && new Date(c.original_end_date) <= today) ||
    (c.extension && c.extension !== "")
  );
  const renewedCustomers = dueCustomers.filter(c => c.status !== "Ended" || (c.extension && c.extension !== ""));
  const dueReformers = dueCustomers.reduce((s, c) => s + machineCount(c.machine), 0);
  const renewedReformers = renewedCustomers.reduce((s, c) => s + machineCount(c.machine), 0);
  return {
    hasData: dueCustomers.length > 0,
    customerRate: dueCustomers.length > 0 ? (renewedCustomers.length / dueCustomers.length) * 100 : null,
    fleetRate: dueReformers > 0 ? (renewedReformers / dueReformers) * 100 : null,
    dueCustomers: dueCustomers.length, renewedCustomers: renewedCustomers.length,
    dueReformers: dueReformers, renewedReformers: renewedReformers,
  };
}

function calcUpcomingRenewals(customers, extra) {
  const groups = {};
  customers.filter(c => c.status === "Active" || c.status === "Ending").forEach(c => {
    const q = quarterOf(c.renewal);
    if (!q) return;
    if (!groups[q.key]) groups[q.key] = { label: q.label, sortKey: q.sortKey, count: 0 };
    groups[q.key].count += machineCount(c.machine);
  });
  if (extra && extra.quarterKey) {
    if (!groups[extra.quarterKey]) groups[extra.quarterKey] = { label: extra.quarterLabel, sortKey: extra.sortKey, count: 0 };
    groups[extra.quarterKey].count += extra.count;
  }
  return Object.values(groups).sort((a, b) => a.sortKey - b.sortKey);
}

function computeActualPayoffDate(f, paidAmount) {
  const monthlyPayment = parseFloat(f.monthly_payment) || 0;
  const totalAmount = parseFloat(f.total_amount) || 0;
  const paymentDay = parseInt(f.payment_day) || 1;
  if (monthlyPayment <= 0) return null;
  const remaining = Math.max(0, totalAmount - (paidAmount || 0));
  if (remaining <= 0) return new Date();
  const monthsRemaining = Math.ceil(remaining / monthlyPayment);
  const today = new Date();
  let nextPaymentDate = new Date(today.getFullYear(), today.getMonth(), paymentDay);
  if (nextPaymentDate < today) nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
  nextPaymentDate.setMonth(nextPaymentDate.getMonth() + (monthsRemaining - 1));
  return nextPaymentDate;
}

function calcQuarterlyCapacity(renewalsList, machineFinanceList, financePaymentsList, currentRepayments, currentIncome, avgRatePerReformer, currentEarning, newRepaymentTerm, fleetRenewalRate) {
  const rr = (fleetRenewalRate == null ? 100 : fleetRenewalRate) / 100;
  let accumulatedNonRenewals = 0;
  return renewalsList.map((r, idx) => {
    if (idx > 0) {
      accumulatedNonRenewals += Math.round(renewalsList[idx - 1].count * (1 - rr));
    }
    const year = Math.floor(r.sortKey / 10);
    const q = r.sortKey % 10;
    const quarterStart = new Date(year, (q - 1) * 3, 1);

    let projectedRepayments = currentRepayments;
    if (newRepaymentTerm && newRepaymentTerm > 0) {
      const nd = new Date(); nd.setMonth(nd.getMonth() + newRepaymentTerm);
      const newFinanceEnd = new Date(nd.getFullYear(), nd.getMonth(), 1);
      if (newFinanceEnd <= quarterStart) {
        // the newly-added repayment has finished by this quarter — it was baked into currentRepayments, remove it
        projectedRepayments = currentRepayments - (window.__newRepaymentAmount || 0);
      }
    }
    (machineFinanceList || []).forEach(f => {
      const paidForF = (financePaymentsList || []).filter(p => p.finance_id === f.id).reduce((s, p) => s + parseFloat(p.amount), 0);
      const payoff = computeActualPayoffDate(f, paidForF);
      if (payoff && payoff <= quarterStart) {
        projectedRepayments -= parseFloat(f.monthly_payment) || 0;
      }
    });
    projectedRepayments = Math.max(0, projectedRepayments);

    const minReformersRequired = avgRatePerReformer > 0 ? Math.ceil(projectedRepayments / avgRatePerReformer) : 0;
    const canReturn = Math.max(0, (currentEarning || 0) - minReformersRequired);
    const canReturnAfterChurn = Math.max(0, canReturn - accumulatedNonRenewals);
    const exceeds = r.count > canReturn;

    return { ...r, projectedRepayments, minReformersRequired, canReturn, canReturnAfterChurn, exceeds };
  });
}

function InvestmentDashboard({ customers, machines, monthlyOut }) {
  const [emergencyPot, setEmergencyPot] = useState(0);
  const [editingPot, setEditingPot] = useState(false);
  const [potInput, setPotInput] = useState("");
  const [includePending, setIncludePending] = useState(false);

  useEffect(() => {
    supabase.from("business_settings").select("value").eq("key", "emergency_cash_pot").maybeSingle().then(({ data }) => {
      if (data) setEmergencyPot(parseFloat(data.value) || 0);
    });
  }, []);

  const savePot = async () => {
    const v = parseFloat(potInput) || 0;
    setEmergencyPot(v);
    await supabase.from("business_settings").upsert({ key: "emergency_cash_pot", value: String(v) }, { onConflict: "key" });
    setEditingPot(false);
  };

  const [machineFinanceList, setMachineFinanceList] = useState([]);
  const [financePaymentsList, setFinancePaymentsList] = useState([]);
  useEffect(() => {
    supabase.from("machine_finance").select("*").then(({ data }) => {
      if (data) setMachineFinanceList(data);
    });
    supabase.from("machine_finance_payments").select("*").then(({ data }) => {
      if (data) setFinancePaymentsList(data);
    });
  }, []);

  const pendingCustomers = customers.filter(c => c.status === "Pending");
  const pendingReformers = pendingCustomers.reduce((s, c) => s + machineCount(c.machine), 0);
  const pendingIncome = pendingCustomers.reduce((s, c) => {
    const v = parseFloat((c.monthly || "0").replace(/[£,]/g, ""));
    return s + (isNaN(v) ? 0 : v);
  }, 0);
  const pendingFinanceAgreements = machineFinanceList.filter(f => f.status === "Pending");
  const pendingFinanceAmount = pendingFinanceAgreements.reduce((s, f) => s + (parseFloat(f.monthly_payment) || 0), 0);

  const effectiveCustomers = includePending
    ? customers.map(c => c.status === "Pending" ? { ...c, status: "Active" } : c)
    : customers;
  const effectiveMonthlyOut = includePending ? monthlyOut + pendingFinanceAmount : monthlyOut;

  const fleet = calcFleetMetrics(effectiveCustomers, machines, 0, includePending ? pendingReformers : 0);
  const finance = calcFinanceMetrics(effectiveCustomers, effectiveMonthlyOut, emergencyPot);
  const retention = calcRetentionMetrics(effectiveCustomers);
  const renewals = calcUpcomingRenewals(effectiveCustomers);

  const quarterlyCapacity = calcQuarterlyCapacity(renewals, machineFinanceList, financePaymentsList, finance.monthlyRepayments, finance.monthlyIncome, finance.avgRatePerReformer, fleet.earning, 0, retention.customerRate);

  const blankForm = { studio: "", reformers: "", machineType: "Align R8 Pro", term: "12", purchaseCost: "", monthlyIncome: "", monthlyRepayment: "", depositReceived: false };
  const [form, setForm] = useState(blankForm);
  const [analysis, setAnalysis] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const analyse = () => {
    const numReformers = parseInt(form.reformers) || 0;
    const monthlyIncomeAdd = parseFloat(form.monthlyIncome) || 0;
    const monthlyRepaymentAdd = parseFloat(form.monthlyRepayment) || 0;
    const termMonths = parseInt(form.term) || 0;

    const newFleet = calcFleetMetrics(effectiveCustomers, machines, numReformers, numReformers + (includePending ? pendingReformers : 0));
    const newFinance = calcFinanceMetrics(effectiveCustomers, effectiveMonthlyOut, emergencyPot, monthlyIncomeAdd, monthlyRepaymentAdd, numReformers);

    let renewalQuarter = null;
    if (termMonths > 0) {
      const d = new Date(); d.setMonth(d.getMonth() + termMonths);
      renewalQuarter = quarterOf(d.toISOString().split("T")[0]);
    }
    const newRenewals = calcUpcomingRenewals(effectiveCustomers, renewalQuarter ? { quarterKey: renewalQuarter.key, quarterLabel: renewalQuarter.label, sortKey: renewalQuarter.sortKey, count: numReformers } : null);
    window.__newRepaymentAmount = monthlyRepaymentAdd;
    const repaymentTermMonths = parseInt(form.repaymentTerm) || 0;
    const newQuarterlyCapacity = calcQuarterlyCapacity(newRenewals, machineFinanceList, financePaymentsList, newFinance.monthlyRepayments, newFinance.monthlyIncome, newFinance.avgRatePerReformer, newFleet.earning, repaymentTermMonths, retention.customerRate);

    const bufferOk = newFinance.emergencyBuffer !== null && newFinance.emergencyBuffer >= 3;
    const coverageOk = newFinance.coverage <= 70;
    const depositOk = form.depositReceived;

    let verdict = "red";
    if (depositOk && bufferOk && coverageOk) verdict = "green";
    else if (depositOk && (bufferOk || coverageOk)) verdict = "orange";

    setAnalysis({ fleet: newFleet, finance: newFinance, retention: calcRetentionMetrics(effectiveCustomers), renewals: newRenewals, quarterlyCapacity: newQuarterlyCapacity, rules: { depositOk, bufferOk, coverageOk }, verdict });
  };

  const resetAnalysis = () => { setAnalysis(null); setForm(blankForm); };

  const fmt = (n) => "£" + Math.round(n || 0).toLocaleString();
  const pct = (n) => (n === null || n === undefined || isNaN(n)) ? "—" : Math.round(n) + "%";
  const months = (n) => (n === null || n === undefined || isNaN(n) || !isFinite(n)) ? "—" : n.toFixed(1) + " mo";

  const MetricCard = ({ label, value, sub, color }) => (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={color ? { ...styles.statValue, color: color } : styles.statValue}>{value}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <p style={styles.sectionTitle}>Investment Control Centre</p>

      {pendingCustomers.length > 0 && (
        <div style={{ ...styles.card, marginBottom: 24, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
          <div onClick={() => setIncludePending(v => !v)} style={{ width: 18, height: 18, borderRadius: 2, border: "1px solid " + (includePending ? "#4CAF82" : BORDER), background: includePending ? "#4CAF82" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {includePending && <span style={{ color: "#111", fontSize: 11, fontWeight: "bold" }}>✓</span>}
          </div>
          <div onClick={() => setIncludePending(v => !v)} style={{ cursor: "pointer", flex: 1 }}>
            <span style={{ fontSize: 13, color: TEXT }}>Include Pending</span>
            <span style={{ fontSize: 12, color: MUTED, marginLeft: 10 }}>
              {pendingCustomers.length} customer{pendingCustomers.length !== 1 ? "s" : ""} · {pendingReformers} reformer{pendingReformers !== 1 ? "s" : ""} · +£{Math.round(pendingIncome).toLocaleString()}/mo income{pendingFinanceAgreements.length > 0 ? " · +£" + Math.round(pendingFinanceAmount).toLocaleString() + "/mo repayments (" + pendingFinanceAgreements.length + " agreement" + (pendingFinanceAgreements.length !== 1 ? "s" : "") + ")" : ""}
            </span>
          </div>
          {includePending && <span style={styles.pill("#4CAF82")}>Future Outlook</span>}
        </div>
      )}

      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12 }}>Fleet</div>
      <div style={styles.grid3}>
        <MetricCard label="Total Reformers Owned" value={fleet.totalReformers} />
        <MetricCard label="Reformers Currently Earning" value={fleet.earning} />
        <MetricCard label="Live Fleet Utilisation" value={pct(fleet.utilisation)} color={fleet.utilisation >= 80 ? "#4CAF82" : fleet.utilisation >= 50 ? GOLD : MAHOGANY} />
      </div>

      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Finance</div>
      <div style={styles.grid3}>
        <MetricCard label="Net Monthly" value={fmt(Math.max(0, finance.monthlyIncome - finance.monthlyRepayments))} sub={fmt(finance.monthlyIncome) + " revenue · " + fmt(finance.monthlyRepayments) + " outgoings"} />
        <MetricCard label="Monthly Contracted Rental Income" value={fmt(finance.monthlyIncome)} />
        <MetricCard label="Monthly Fixed Repayments" value={fmt(finance.monthlyRepayments)} />
        <MetricCard label="Finance Coverage" value={pct(finance.coverage)} color={finance.coverage <= 70 ? "#4CAF82" : MAHOGANY} sub={finance.coverage <= 70 ? "Within policy (≤70%)" : "Above policy (≤70%)"} />
        <MetricCard label="Minimum Reformers Required" value={finance.minReformersRequired !== null ? finance.minReformersRequired : "—"} sub={"To cover fixed repayments" + (finance.minReformersRequired !== null ? " · " + Math.max(0, fleet.earning - finance.minReformersRequired) + " could return" : "")} />
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Emergency Buffer</div>
          {editingPot ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <span style={{ color: MUTED }}>£</span>
              <input autoFocus value={potInput} onChange={e => setPotInput(e.target.value)} style={{ ...inputStyle, width: 100 }} placeholder="Cash pot" />
              <button onClick={savePot} style={{ ...styles.btnGhost, fontSize: 10, padding: "4px 10px" }}>Save</button>
            </div>
          ) : (
            <div>
              <div style={{ ...styles.statValue, color: (finance.emergencyBuffer !== null && finance.emergencyBuffer >= 3) ? "#4CAF82" : MAHOGANY }}>{months(finance.emergencyBuffer)}</div>
              <div style={styles.statSub}>Cash pot: {fmt(emergencyPot)} · <span onClick={() => { setPotInput(String(emergencyPot)); setEditingPot(true); }} style={{ color: GOLD, cursor: "pointer" }}>edit</span></div>
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Retention</div>
      <div style={styles.grid2}>
        <MetricCard label="Fleet Renewal Rate" value={retention.hasData ? pct(retention.fleetRate) : "No Data Yet"} sub={retention.hasData ? (retention.renewedReformers + " of " + retention.dueReformers + " reformers") : ""} />
        <MetricCard label="Customer Renewal Rate" value={retention.hasData ? pct(retention.customerRate) : "No Data Yet"} sub={retention.hasData ? (retention.renewedCustomers + " of " + retention.dueCustomers + " customers") : ""} />
      </div>

      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Upcoming Renewals · Capacity</div>
      <div style={{ ...styles.card, marginBottom: 24 }}>
        <table style={styles.table}>
          <thead><tr>{["Quarter", "Reformers Due", "Projected Repayments", "Reformers That Can Return", "After Renewal Rate", "Status"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>
            {quarterlyCapacity.length === 0 && <tr><td colSpan={5} style={{ ...styles.td, color: MUTED }}>No upcoming renewals</td></tr>}
            {quarterlyCapacity.map(r => (
              <tr key={r.label}>
                <td style={styles.td}>{r.label}</td>
                <td style={{ ...styles.td, color: r.exceeds ? MAHOGANY : GOLD }}>{r.count} Reformer{r.count !== 1 ? "s" : ""}</td>
                <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{fmt(r.projectedRepayments)}</td>
                <td style={{ ...styles.td, color: TEXT }}>{r.canReturn} Reformer{r.canReturn !== 1 ? "s" : ""}</td>
                <td style={{ ...styles.td, color: GOLD }}>{r.canReturnAfterChurn} Reformer{r.canReturnAfterChurn !== 1 ? "s" : ""}</td>
                <td style={styles.td}>
                  <span style={styles.pill(r.exceeds ? MAHOGANY : "#4CAF82")}>{r.exceeds ? "Exceeds Capacity" : "Within Capacity"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "10px 24px", fontSize: 11, color: MUTED, borderTop: "1px solid " + BORDER }}>
          Reformers That Can Return = your currently earning reformers minus the minimum needed to cover that quarter's projected repayments.
        </div>
      </div>

      <p style={styles.sectionTitle}>Investment Analysis</p>
      <div style={{ ...styles.card, marginBottom: 24 }}>
        <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <div style={fieldWrap}><label style={labelStyle}>Studio Name</label><input value={form.studio} onChange={e => setF("studio", e.target.value)} style={inputStyle} /></div>
          <div style={fieldWrap}><label style={labelStyle}>Number of Reformers</label><input type="number" value={form.reformers} onChange={e => setF("reformers", e.target.value)} style={inputStyle} /></div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Machine Type</label>
            <select value={form.machineType} onChange={e => setF("machineType", e.target.value)} style={selectStyle}>
              <option>Align R8 Pro</option><option>Align C8 Pro</option><option>RR Chair</option>
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelStyle}>Contract Length (months)</label>
            <select value={form.term} onChange={e => setF("term", e.target.value)} style={selectStyle}>
              {["4", "6", "8", "12", "14", "24"].map(o => <option key={o} value={o}>{o} months</option>)}
            </select>
          </div>
          <div style={fieldWrap}><label style={labelStyle}>Purchase Cost (£)</label><input type="number" value={form.purchaseCost} onChange={e => setF("purchaseCost", e.target.value)} style={inputStyle} /></div>
          <div style={fieldWrap}><label style={labelStyle}>Monthly Rental Income (£)</label><input type="number" value={form.monthlyIncome} onChange={e => setF("monthlyIncome", e.target.value)} style={inputStyle} /></div>
          <div style={fieldWrap}><label style={labelStyle}>New Monthly Finance Repayment (£)</label><input type="number" value={form.monthlyRepayment} onChange={e => setF("monthlyRepayment", e.target.value)} style={inputStyle} /></div>
          <div style={fieldWrap}><label style={labelStyle}>Finance Term (months)</label><input type="number" value={form.repaymentTerm} onChange={e => setF("repaymentTerm", e.target.value)} style={inputStyle} placeholder="e.g. 24" /></div>
          <div style={{ ...fieldWrap, display: "flex", alignItems: "center", gap: 10, marginTop: 20 }}>
            <div onClick={() => setF("depositReceived", !form.depositReceived)} style={{ width: 18, height: 18, borderRadius: 2, border: "1px solid " + (form.depositReceived ? "#4CAF82" : BORDER), background: form.depositReceived ? "#4CAF82" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {form.depositReceived && <span style={{ color: "#111", fontSize: 11, fontWeight: "bold" }}>✓</span>}
            </div>
            <span style={{ fontSize: 12, color: MUTED }}>Deposit Received</span>
          </div>
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 10 }}>
            <button style={{ ...styles.btn, padding: "11px 32px" }} onClick={analyse}>Analyse Investment</button>
            {analysis && <button style={styles.btnGhost} onClick={resetAnalysis}>Clear Analysis</button>}
          </div>
        </div>
      </div>

      {analysis && (
        <div>
          <p style={styles.sectionTitle}>Projected Impact{form.studio ? " · " + form.studio : ""}</p>

          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12 }}>Updated Fleet</div>
          <div style={styles.grid3}>
            <MetricCard label="Total Reformers Owned" value={analysis.fleet.totalReformers} />
            <MetricCard label="Reformers Currently Earning" value={analysis.fleet.earning} />
            <MetricCard label="Live Fleet Utilisation" value={pct(analysis.fleet.utilisation)} />
          </div>

          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Updated Finance</div>
          <div style={styles.grid3}>
            <MetricCard label="Net Monthly" value={fmt(Math.max(0, analysis.finance.monthlyIncome - analysis.finance.monthlyRepayments))} sub={fmt(analysis.finance.monthlyIncome) + " revenue · " + fmt(analysis.finance.monthlyRepayments) + " outgoings"} />
            <MetricCard label="Monthly Contracted Rental Income" value={fmt(analysis.finance.monthlyIncome)} />
            <MetricCard label="Monthly Fixed Repayments" value={fmt(analysis.finance.monthlyRepayments)} />
            <MetricCard label="Finance Coverage" value={pct(analysis.finance.coverage)} color={analysis.finance.coverage <= 70 ? "#4CAF82" : MAHOGANY} />
            <MetricCard label="Minimum Reformers Required" value={analysis.finance.minReformersRequired !== null ? analysis.finance.minReformersRequired : "—"} />
            <MetricCard label="Emergency Buffer" value={months(analysis.finance.emergencyBuffer)} color={(analysis.finance.emergencyBuffer !== null && analysis.finance.emergencyBuffer >= 3) ? "#4CAF82" : MAHOGANY} />
          </div>

          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Updated Retention</div>
          <div style={styles.grid2}>
            <MetricCard label="Fleet Renewal Rate" value={analysis.retention.hasData ? pct(analysis.retention.fleetRate) : "No Data Yet"} sub={analysis.retention.hasData ? (analysis.retention.renewedReformers + " of " + analysis.retention.dueReformers + " reformers") : ""} />
            <MetricCard label="Customer Renewal Rate" value={analysis.retention.hasData ? pct(analysis.retention.customerRate) : "No Data Yet"} sub={analysis.retention.hasData ? (analysis.retention.renewedCustomers + " of " + analysis.retention.dueCustomers + " customers") : ""} />
          </div>

          <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: GOLD, marginBottom: 12, marginTop: 8 }}>Updated Upcoming Renewals \u00b7 Capacity</div>
          <div style={{ ...styles.card, marginBottom: 24 }}>
            <table style={styles.table}>
              <thead><tr>{["Quarter", "Reformers Due", "Projected Repayments", "Reformers That Can Return", "After Renewal Rate", "Status"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
              <tbody>
                {(analysis.quarterlyCapacity || []).length === 0 && <tr><td colSpan={6} style={{ ...styles.td, color: MUTED }}>No upcoming renewals</td></tr>}
                {(analysis.quarterlyCapacity || []).map(r => (
                  <tr key={r.label}>
                    <td style={styles.td}>{r.label}</td>
                    <td style={{ ...styles.td, color: r.exceeds ? MAHOGANY : GOLD }}>{r.count} Reformer{r.count !== 1 ? "s" : ""}</td>
                    <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{fmt(r.projectedRepayments)}</td>
                    <td style={{ ...styles.td, color: TEXT }}>{r.canReturn} Reformer{r.canReturn !== 1 ? "s" : ""}</td>
                    <td style={{ ...styles.td, color: GOLD }}>{r.canReturnAfterChurn} Reformer{r.canReturnAfterChurn !== 1 ? "s" : ""}</td>
                    <td style={styles.td}>
                      <span style={styles.pill(r.exceeds ? MAHOGANY : "#4CAF82")}>{r.exceeds ? "Exceeds Capacity" : "Within Capacity"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...styles.card, marginBottom: 24 }}>
            <div style={styles.cardHeader}><span style={styles.cardTitle}>Investment Rules</span></div>
            <div style={{ padding: 24 }}>
              {[
                { label: "Deposit Received", ok: analysis.rules.depositOk },
                { label: "Emergency Buffer ≥ 3 Months", ok: analysis.rules.bufferOk },
                { label: "Finance Coverage ≤ 70%", ok: analysis.rules.coverageOk },
              ].map(r => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid " + BORDER, fontSize: 13 }}>
                  <span style={{ color: MUTED }}>{r.label}</span>
                  <span style={{ color: r.ok ? "#4CAF82" : MAHOGANY }}>{r.ok ? "✅ Pass" : "✕ Fail"}</span>
                </div>
              ))}
              <div style={{
                marginTop: 20, padding: "16px 20px", borderRadius: 4, textAlign: "center", fontSize: 15, letterSpacing: "0.06em", fontFamily: "'Cormorant Garant', serif",
                background: analysis.verdict === "green" ? "#4CAF8218" : analysis.verdict === "orange" ? "#E8A83818" : MAHOGANY + "18",
                border: "1px solid " + (analysis.verdict === "green" ? "#4CAF8266" : analysis.verdict === "orange" ? "#E8A83866" : MAHOGANY + "66"),
                color: analysis.verdict === "green" ? "#4CAF82" : analysis.verdict === "orange" ? "#E8A838" : MAHOGANY
              }}>
                {analysis.verdict === "green" && "🟢 Within Investment Policy"}
                {analysis.verdict === "orange" && "🟠 Review Investment"}
                {analysis.verdict === "red" && "🔴 Outside Investment Policy"}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
